import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Home, Building2, User, Phone, Mail, MapPin,
  Edit, Save, Loader2, PoundSterling, FileText, Calendar,
  BedDouble, Bath, Sofa, Car, TreePine, Ruler, Zap,
  Upload, AlertCircle, CheckCircle, Shield, Key, Receipt, Plus, Clock,
  Send, RefreshCw, Copy, Check, LayoutDashboard, ClipboardList, 
  CreditCard, Percent, History, Trash2, FilePlus, AlertTriangle,
  MessageSquare, StickyNote, UserPlus, Landmark, Users, Eye
} from "lucide-react";
import { API } from "@/App";

// EPC Rating color mapping
const EPC_COLORS = {
  'A': 'bg-green-600',
  'B': 'bg-green-500',
  'C': 'bg-lime-500',
  'D': 'bg-yellow-500',
  'E': 'bg-orange-500',
  'F': 'bg-orange-600',
  'G': 'bg-red-600'
};

// Letting Menu Items
const LETTING_MENU_ITEMS = [
  { id: 'overview', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'contacts', label: 'Contacts', icon: User },
  { id: 'tenant', label: 'Tenant', icon: Home },
  { id: 'tenant_history', label: 'Past Tenants', icon: Users },
  { id: 'details', label: 'Letting Details', icon: ClipboardList },
  { id: 'receipts', label: 'Tenant Receipts', icon: Receipt },
  { id: 'statements', label: 'Landlord Statements', icon: FileText },
  { id: 'rent_schedule', label: 'Rent Schedule', icon: Calendar },
  { id: 'landlord_fees', label: 'Landlord Fees', icon: Percent },
  { id: 'bank_details', label: 'Bank Details', icon: Landmark },
  { id: 'inspections', label: 'Inspections', icon: ClipboardList },
  { id: 'notes', label: 'Notes', icon: StickyNote },
  { id: 'compliance', label: 'Compliance', icon: Shield },
];

export const PropertyDetailPage = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [property, setProperty] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [landlord, setLandlord] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [activeSection, setActiveSection] = useState("overview");
  
  // Receipts state
  const [receipts, setReceipts] = useState([]);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptForm, setReceiptForm] = useState({
    amount: "",
    date_received: new Date().toISOString().split('T')[0],
    payment_method: "bank_transfer",
    payment_reference: "",
    paid_direct_to_landlord: false
  });
  const [submittingReceipt, setSubmittingReceipt] = useState(false);
  
  // Statements state
  const [statements, setStatements] = useState([]);
  
  // Notes state
  const [notes, setNotes] = useState([]);
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [newNote, setNewNote] = useState({ content: "", is_alert: false });
  const [savingNote, setSavingNote] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [showEditNoteModal, setShowEditNoteModal] = useState(false);
  
  // Tenant history state
  const [tenantHistory, setTenantHistory] = useState([]);
  
  // Add Tenant state
  const [showAddTenantModal, setShowAddTenantModal] = useState(false);
  const [savingTenant, setSavingTenant] = useState(false);
  const [newTenant, setNewTenant] = useState({
    name: "",
    email: "",
    phone: "",
    mobile: "",
    tenancy_start: "",
    tenancy_end: "",
    rent_amount: "",
    deposit_amount: "",
    payment_day: "1"
  });
  
  // Allocate Tenant state
  const [showAllocateTenantModal, setShowAllocateTenantModal] = useState(false);
  const [availableTenants, setAvailableTenants] = useState([]);
  const [tenantSearchQuery, setTenantSearchQuery] = useState("");
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [loadingTenants, setLoadingTenants] = useState(false);
  
  // Inspections state
  const [inspections, setInspections] = useState([]);
  const [showAddInspectionModal, setShowAddInspectionModal] = useState(false);
  const [savingInspection, setSavingInspection] = useState(false);
  const [newInspection, setNewInspection] = useState({
    inspection_type: "routine",
    scheduled_date: "",
    frequency_months: 3,
    alert_days_before: 14,
    notes: ""
  });
  
  // Inspection settings state
  const [inspectionSettings, setInspectionSettings] = useState({
    default_frequency_months: 3,
    default_alert_days: 14
  });
  const [editingInspectionSettings, setEditingInspectionSettings] = useState(false);
  const [savingInspectionSettings, setSavingInspectionSettings] = useState(false);
  
  // Portal invite states
  const [sendingTenantInvite, setSendingTenantInvite] = useState(false);
  const [sendingLandlordInvite, setSendingLandlordInvite] = useState(false);
  const [copiedLink, setCopiedLink] = useState(null);
  
  // Landlord allocation states
  const [showAllocateLandlordModal, setShowAllocateLandlordModal] = useState(false);
  const [landlordSearchTerm, setLandlordSearchTerm] = useState("");
  const [landlordSearchResults, setLandlordSearchResults] = useState([]);
  const [searchingLandlords, setSearchingLandlords] = useState(false);
  const [allocatingLandlord, setAllocatingLandlord] = useState(false);

  useEffect(() => {
    fetchData();
  }, [propertyId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [propRes, receiptsRes, statementsRes, notesRes, inspectionsRes, historyRes] = await Promise.all([
        axios.get(`${API}/lettings/properties/${propertyId}`, { headers }),
        axios.get(`${API}/rent/lettings/properties/${propertyId}/receipts`, { headers }).catch(() => ({ data: { receipts: [] } })),
        axios.get(`${API}/landlord-statements/property/${propertyId}`, { headers }).catch(() => ({ data: { statements: [] } })),
        axios.get(`${API}/lettings/properties/${propertyId}/notes`, { headers }).catch(() => ({ data: { notes: [] } })),
        axios.get(`${API}/lettings/properties/${propertyId}/inspections`, { headers }).catch(() => ({ data: { inspections: [] } })),
        axios.get(`${API}/lettings/properties/${propertyId}/tenant-history`, { headers }).catch(() => ({ data: { tenant_history: [] } }))
      ]);
      
      setProperty(propRes.data);
      setTenant(propRes.data.current_tenant || null);
      setLandlord(propRes.data.landlord || null);
      setEditData(propRes.data);
      setReceipts(receiptsRes.data?.receipts || []);
      setStatements(statementsRes.data?.statements || []);
      setNotes(notesRes.data?.notes || []);
      setInspections(inspectionsRes.data?.inspections || []);
      setTenantHistory(historyRes.data?.tenant_history || propRes.data?.tenant_history || []);
      
      // Load inspection settings from property
      setInspectionSettings({
        default_frequency_months: propRes.data.default_frequency_months || 3,
        default_alert_days: propRes.data.default_alert_days || 14
      });
      
      // Update default values for new inspections based on property settings
      setNewInspection(prev => ({
        ...prev,
        frequency_months: propRes.data.default_frequency_months || 3,
        alert_days_before: propRes.data.default_alert_days || 14
      }));
    } catch (error) {
      console.error("Error fetching property:", error);
      toast.error("Failed to load property details");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/lettings/properties/${propertyId}`, editData, { headers });
      toast.success("Property details updated");
      setProperty({ ...property, ...editData });
      setEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleEpcUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/lettings/properties/${propertyId}/upload-epc`,
        formData,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success("EPC document uploaded");
      setProperty({ ...property, epc_document_url: response.data.url });
      setEditData({ ...editData, epc_document_url: response.data.url });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload");
    } finally {
      setUploading(false);
    }
  };

  // Handle record receipt
  const handleRecordReceipt = async () => {
    if (!receiptForm.amount || parseFloat(receiptForm.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setSubmittingReceipt(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${API}/rent/lettings/properties/${propertyId}/receipts`,
        {
          ...receiptForm,
          amount: parseFloat(receiptForm.amount),
          property_id: propertyId,
          tenant_id: tenant?.tenant_id
        },
        { headers }
      );
      
      toast.success("Receipt recorded successfully");
      setShowReceiptModal(false);
      setReceiptForm({
        amount: "",
        date_received: new Date().toISOString().split('T')[0],
        payment_method: "bank_transfer",
        payment_reference: "",
        paid_direct_to_landlord: false
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record receipt");
    } finally {
      setSubmittingReceipt(false);
    }
  };

  // Handle add note
  const handleAddNote = async () => {
    if (!newNote.content.trim()) {
      toast.error("Please enter note content");
      return;
    }
    
    setSavingNote(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${API}/lettings/properties/${propertyId}/notes`,
        newNote,
        { headers }
      );
      toast.success("Note added");
      setShowAddNoteModal(false);
      setNewNote({ content: "", is_alert: false });
      fetchData();
    } catch (error) {
      toast.error("Failed to add note");
    } finally {
      setSavingNote(false);
    }
  };

  // Handle edit note
  const handleEditNote = async () => {
    if (!editingNote?.content?.trim()) {
      toast.error("Please enter note content");
      return;
    }
    
    setSavingNote(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(
        `${API}/lettings/properties/${propertyId}/notes/${editingNote.note_id}`,
        { content: editingNote.content, is_alert: editingNote.is_alert },
        { headers }
      );
      toast.success("Note updated");
      setShowEditNoteModal(false);
      setEditingNote(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update note");
    } finally {
      setSavingNote(false);
    }
  };

  // Handle delete note
  const handleDeleteNote = async (noteId) => {
    if (!confirm("Are you sure you want to delete this note?")) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(
        `${API}/lettings/properties/${propertyId}/notes/${noteId}`,
        { headers }
      );
      toast.success("Note deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete note");
    }
  };

  // Add new tenant to property
  const handleAddTenant = async () => {
    if (!newTenant.name.trim()) {
      toast.error("Please enter tenant name");
      return;
    }
    
    setSavingTenant(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const tenantData = {
        name: newTenant.name.trim(),
        email: newTenant.email.trim() || null,
        phone: newTenant.phone.trim() || null,
        mobile: newTenant.mobile.trim() || null,
        property_id: propertyId,
        property_address: property.address,
        tenancy_start: newTenant.tenancy_start || null,
        tenancy_end: newTenant.tenancy_end || null,
        rent_amount: parseFloat(newTenant.rent_amount) || property.rent_amount || 0,
        deposit_amount: parseFloat(newTenant.deposit_amount) || 0,
        payment_day: parseInt(newTenant.payment_day) || 1,
        status: "active"
      };
      
      await axios.post(`${API}/lettings/tenants`, tenantData, { headers });
      
      toast.success("Tenant added successfully!");
      setShowAddTenantModal(false);
      setNewTenant({
        name: "",
        email: "",
        phone: "",
        mobile: "",
        tenancy_start: "",
        tenancy_end: "",
        rent_amount: "",
        deposit_amount: "",
        payment_day: "1"
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add tenant");
    } finally {
      setSavingTenant(false);
    }
  };

  // Fetch available tenants for allocation
  const fetchAvailableTenants = async (search = "") => {
    setLoadingTenants(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/lettings/tenants?status=looking&search=${encodeURIComponent(search)}`, { headers });
      setAvailableTenants(res.data?.tenants || res.data || []);
    } catch (error) {
      console.error("Failed to fetch tenants:", error);
      setAvailableTenants([]);
    } finally {
      setLoadingTenants(false);
    }
  };

  // Allocate existing tenant to property
  const handleAllocateTenant = async () => {
    if (!selectedTenantId) {
      toast.error("Please select a tenant");
      return;
    }
    
    setSavingTenant(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Update tenant with property assignment
      await axios.put(`${API}/lettings/tenants/${selectedTenantId}`, {
        property_id: propertyId,
        property_address: property.address,
        status: "active"
      }, { headers });
      
      toast.success("Tenant allocated to property!");
      setShowAllocateTenantModal(false);
      setSelectedTenantId(null);
      setTenantSearchQuery("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to allocate tenant");
    } finally {
      setSavingTenant(false);
    }
  };

  // Handle add inspection
  const handleAddInspection = async () => {
    if (!newInspection.scheduled_date) {
      toast.error("Please select a scheduled date");
      return;
    }
    
    setSavingInspection(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/lettings/properties/${propertyId}/inspections`, newInspection, { headers });
      
      toast.success("Inspection scheduled!");
      setShowAddInspectionModal(false);
      setNewInspection({
        inspection_type: "routine",
        scheduled_date: "",
        frequency_months: inspectionSettings.default_frequency_months,
        alert_days_before: inspectionSettings.default_alert_days,
        notes: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to schedule inspection");
    } finally {
      setSavingInspection(false);
    }
  };

  // Handle complete inspection
  const handleCompleteInspection = async (inspectionId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/lettings/properties/${propertyId}/inspections/${inspectionId}/complete`, {}, { headers });
      toast.success("Inspection marked as complete");
      fetchData();
    } catch (error) {
      toast.error("Failed to update inspection");
    }
  };

  // Handle delete inspection
  const handleDeleteInspection = async (inspectionId) => {
    if (!confirm("Are you sure you want to delete this inspection?")) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/lettings/properties/${propertyId}/inspections/${inspectionId}`, { headers });
      toast.success("Inspection deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete inspection");
    }
  };

  // Handle save inspection settings
  const handleSaveInspectionSettings = async () => {
    setSavingInspectionSettings(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/lettings/properties/${propertyId}/inspection-settings`, inspectionSettings, { headers });
      
      toast.success("Inspection settings updated!");
      setEditingInspectionSettings(false);
      
      // Update the newInspection defaults with the saved settings
      setNewInspection(prev => ({
        ...prev,
        frequency_months: inspectionSettings.default_frequency_months,
        alert_days_before: inspectionSettings.default_alert_days
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save inspection settings");
    } finally {
      setSavingInspectionSettings(false);
    }
  };

  // Send tenant portal invite
  const handleSendTenantInvite = async () => {
    // Support both linked tenant and embedded property data
    const tenantEmail = tenant?.email || property?.tenant_email;
    const tenantName = tenant?.name || property?.tenant_name;
    
    if (!tenantEmail) {
      toast.error("No tenant email available");
      return;
    }
    
    setSendingTenantInvite(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      if (tenant?.tenant_id) {
        // Use existing tenant record
        await axios.post(`${API}/lettings/tenants/${tenant.tenant_id}/invite`, {}, { headers });
      } else {
        // Create invite from property data
        await axios.post(`${API}/portal/invite/tenant`, {
          email: tenantEmail,
          name: tenantName,
          property_id: property.property_id,
          property_address: property.address
        }, { headers });
      }
      toast.success("Tenant portal invite sent!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invite");
    } finally {
      setSendingTenantInvite(false);
    }
  };

  // Send landlord portal invite
  const handleSendLandlordInvite = async () => {
    // Support both linked landlord and embedded property data
    const landlordEmail = landlord?.email || property?.landlord_email;
    const landlordName = landlord?.name || property?.landlord_name;
    
    if (!landlordEmail) {
      toast.error("No landlord email available");
      return;
    }
    
    setSendingLandlordInvite(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      if (landlord?.landlord_id && landlord?.email) {
        // Use existing landlord record (only if landlord has email)
        await axios.post(`${API}/lettings/landlords/${landlord.landlord_id}/invite`, {}, { headers });
      } else {
        // Create invite from property data (fallback when landlord has no email)
        await axios.post(`${API}/portal/invite/landlord`, {
          email: landlordEmail,
          name: landlordName,
          property_id: property.property_id,
          property_address: property.address
        }, { headers });
      }
      toast.success("Landlord portal invite sent!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invite");
    } finally {
      setSendingLandlordInvite(false);
    }
  };

  // Search landlords for allocation
  const searchLandlords = async (term) => {
    if (!term || term.length < 2) {
      setLandlordSearchResults([]);
      return;
    }
    setSearchingLandlords(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/lettings/landlords?search=${encodeURIComponent(term)}&per_page=10`, { headers });
      setLandlordSearchResults(res.data.landlords || []);
    } catch (error) {
      console.error("Error searching landlords:", error);
      setLandlordSearchResults([]);
    } finally {
      setSearchingLandlords(false);
    }
  };

  // Allocate landlord to property
  const handleAllocateLandlord = async (selectedLandlord) => {
    setAllocatingLandlord(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/lettings/properties/${propertyId}`, {
        landlord_id: selectedLandlord.landlord_id,
        landlord_name: selectedLandlord.name,
        landlord_email: selectedLandlord.email,
        landlord_phone: selectedLandlord.phone || selectedLandlord.mobile
      }, { headers });
      
      toast.success(`Landlord ${selectedLandlord.name} allocated to this property`);
      setShowAllocateLandlordModal(false);
      setLandlordSearchTerm("");
      setLandlordSearchResults([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to allocate landlord");
    } finally {
      setAllocatingLandlord(false);
    }
  };

  // Remove landlord from property
  const handleRemoveLandlord = async () => {
    if (!confirm("Are you sure you want to remove the landlord from this property?")) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/lettings/properties/${propertyId}`, {
        landlord_id: null
      }, { headers });
      
      toast.success("Landlord removed from property");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove landlord");
    }
  };

  // Copy invite link
  const handleCopyInviteLink = async (type) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const id = type === 'tenant' ? tenant?.tenant_id : landlord?.landlord_id;
      const res = await axios.get(`${API}/lettings/${type}s/${id}/invite-link`, { headers });
      
      await navigator.clipboard.writeText(res.data.registration_link);
      setCopiedLink(type);
      toast.success("Link copied!");
      setTimeout(() => setCopiedLink(null), 3000);
    } catch (error) {
      toast.error("Failed to get link");
    }
  };

  // Calculate receipt totals
  const totalReceived = receipts.reduce((sum, r) => sum + (r.amount || 0), 0);
  const totalAllocated = receipts.reduce((sum, r) => sum + (r.amount_allocated || 0), 0);
  const monthlyRent = property?.rent_amount || tenant?.rent_amount || 0;
  
  // Calculate tenant balance due
  const tenantBalanceDue = monthlyRent - (totalReceived - totalAllocated);
  
  // Get alert notes
  const alertNotes = notes.filter(n => n.is_alert);

  // Generate letting ID from property ID
  const lettingId = property?.letting_id || `L${propertyId?.slice(-4)?.toUpperCase() || '0000'}`;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-lg font-medium">Property not found</p>
          <Button onClick={() => navigate("/admin/lettings")} className="mt-4">
            Back to Lettings
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold text-slate-800">{property.address}</h1>
                  <Badge variant="outline" className="text-xs">#{lettingId}</Badge>
                </div>
                <p className="text-xs text-muted-foreground">{property.postcode}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => toast.info("Create renewal coming soon")}>
                <FilePlus className="w-4 h-4 mr-1" />
                Create Renewal
              </Button>
              {editing ? (
                <>
                  <Button variant="outline" size="sm" onClick={() => { setEditing(false); setEditData(property); }}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSave} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                    Save
                  </Button>
                </>
              ) : (
                <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                  <Edit className="w-4 h-4 mr-1" />
                  Edit
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Alert Banner - Important Notes */}
      {alertNotes.length > 0 && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-3">
            {alertNotes.map((note, idx) => (
              <div key={idx} className="flex items-center gap-2 text-amber-800">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <span className="text-sm font-medium">{note.content}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-6">
          {/* Sidebar - Letting Menu */}
          <div className="w-56 flex-shrink-0">
            <Card className="sticky top-24">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Letting Menu</CardTitle>
              </CardHeader>
              <CardContent className="p-2">
                <nav className="space-y-1">
                  {LETTING_MENU_ITEMS.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeSection === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => setActiveSection(item.id)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
                          isActive 
                            ? 'bg-blue-50 text-blue-700 font-medium' 
                            : 'text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {item.label}
                      </button>
                    );
                  })}
                </nav>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Letting Summary Card - Always visible at top */}
            <Card className="mb-6 border-l-4 border-l-blue-500">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <h2 className="text-xl font-bold">Letting: #{lettingId}</h2>
                      <Badge className={tenant?.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                        {tenant?.status === 'active' ? 'Current' : 'No Active Tenant'}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{property.address}, {property.postcode}</p>
                  </div>
                  {tenantBalanceDue > 0 && (
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Tenant Balance Due:</p>
                      <p className="text-xl font-bold text-red-600">£{tenantBalanceDue.toLocaleString()}</p>
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 pt-4 border-t">
                  <div>
                    <p className="text-xs text-muted-foreground">Property</p>
                    <p className="font-medium text-sm">{property.address?.split(',')[0]}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Tenant</p>
                    <p className="font-medium text-sm text-blue-600 cursor-pointer hover:underline" 
                       onClick={() => tenant && navigate(`/admin/lettings/tenant/${tenant.tenant_id}`)}>
                      {property.tenant_name || tenant?.name || 'No tenant'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Landlord</p>
                    <p className="font-medium text-sm text-blue-600 cursor-pointer hover:underline"
                       onClick={() => landlord && navigate(`/admin/lettings/landlord/${landlord.landlord_id}`)}>
                      {property.landlord_name || landlord?.name || 'No landlord'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Start Date</p>
                    <p className="font-medium text-sm">{property.tenancy_start_date || tenant?.tenancy_start || tenant?.start_date || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">End Date</p>
                    <p className="font-medium text-sm">{property.tenancy_end_date || tenant?.tenancy_end || tenant?.end_date || (property.is_periodic ? 'Periodic' : '-')}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Rent</p>
                    <p className="font-bold text-sm text-emerald-600">£{monthlyRent.toLocaleString()} pcm</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 mt-4 border-t">
                  <div>
                    <p className="text-xs text-muted-foreground">Deposit</p>
                    <p className="font-medium text-sm">£{(tenant?.deposit_amount || tenant?.deposit || property.deposit || 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Letting Fee</p>
                    <p className="font-medium text-sm">£{(property.letting_fee || 0).toLocaleString()}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground mb-1">Management Fee</p>
                    <div className="flex items-center gap-2">
                      <Select 
                        value={property.management_fee_type || "percentage"}
                        onValueChange={async (feeType) => {
                          try {
                            const headers = { Authorization: `Bearer ${token}` };
                            await axios.put(
                              `${API}/lettings/properties/${propertyId}/settings`,
                              { 
                                letting_package: property.letting_package || "fully_managed",
                                management_fee_percent: property.management_fee_percent || 10,
                                management_fee_fixed: property.management_fee_fixed || 0,
                                management_fee_type: feeType,
                                letting_fee: property.letting_fee || 0,
                                vat_registered: property.vat_registered ?? true
                              },
                              { headers }
                            );
                            toast.success("Fee type updated");
                            fetchData();
                          } catch (error) {
                            toast.error("Failed to update fee type");
                          }
                        }}
                      >
                        <SelectTrigger className="h-8 w-[140px] text-sm" data-testid="fee-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percentage">% of Rent</SelectItem>
                          <SelectItem value="fixed">Fixed Amount</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      {property.management_fee_type === "fixed" ? (
                        <div className="flex items-center gap-1">
                          <span className="text-sm text-muted-foreground">£</span>
                          <Input
                            type="number"
                            className="h-8 w-24 text-sm"
                            value={property.management_fee_fixed || 0}
                            onChange={async (e) => {
                              const newValue = parseFloat(e.target.value) || 0;
                              try {
                                const headers = { Authorization: `Bearer ${token}` };
                                await axios.put(
                                  `${API}/lettings/properties/${propertyId}/settings`,
                                  { 
                                    letting_package: property.letting_package || "fully_managed",
                                    management_fee_percent: property.management_fee_percent || 10,
                                    management_fee_fixed: newValue,
                                    management_fee_type: "fixed",
                                    letting_fee: property.letting_fee || 0,
                                    vat_registered: property.vat_registered ?? true
                                  },
                                  { headers }
                                );
                                fetchData();
                              } catch (error) {
                                toast.error("Failed to update fee");
                              }
                            }}
                            data-testid="fixed-fee-input"
                          />
                          <span className="text-xs text-muted-foreground">(one-off)</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            className="h-8 w-20 text-sm"
                            value={property.management_fee_percent || 10}
                            onChange={async (e) => {
                              const newValue = parseFloat(e.target.value) || 0;
                              try {
                                const headers = { Authorization: `Bearer ${token}` };
                                await axios.put(
                                  `${API}/lettings/properties/${propertyId}/settings`,
                                  { 
                                    letting_package: property.letting_package || "fully_managed",
                                    management_fee_percent: newValue,
                                    management_fee_fixed: property.management_fee_fixed || 0,
                                    management_fee_type: "percentage",
                                    letting_fee: property.letting_fee || 0,
                                    vat_registered: property.vat_registered ?? true
                                  },
                                  { headers }
                                );
                                fetchData();
                              } catch (error) {
                                toast.error("Failed to update fee");
                              }
                            }}
                            data-testid="percent-fee-input"
                          />
                          <span className="text-xs text-muted-foreground">% of rent</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Package</p>
                    <Select 
                      value={property.letting_package || "fully_managed"}
                      onValueChange={async (value) => {
                        try {
                          const headers = { Authorization: `Bearer ${token}` };
                          await axios.put(
                            `${API}/lettings/properties/${propertyId}/settings`,
                            { 
                              letting_package: value,
                              management_fee_percent: property.management_fee_percent || 10,
                              letting_fee: property.letting_fee || 0,
                              vat_registered: property.vat_registered ?? true
                            },
                            { headers }
                          );
                          toast.success("Package type updated");
                          fetchData();
                        } catch (error) {
                          toast.error("Failed to update package type");
                        }
                      }}
                    >
                      <SelectTrigger className="h-8 text-sm font-medium" data-testid="package-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="let_only">Tenant Find</SelectItem>
                        <SelectItem value="rent_collect">Rent Collection</SelectItem>
                        <SelectItem value="hybrid">Hybrid</SelectItem>
                        <SelectItem value="fully_managed">Fully Managed</SelectItem>
                        <SelectItem value="premium_managed">Premium Managed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 pt-2 border-t">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Management Service</p>
                        <p className="text-xs text-slate-500">(Enables rent deduction for repairs)</p>
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            const headers = { Authorization: `Bearer ${token}` };
                            await axios.put(
                              `${API}/accounts/property/${propertyId}/management-status?enabled=${!property.management_service_enabled}`,
                              {},
                              { headers }
                            );
                            toast.success(property.management_service_enabled ? "Management service disabled" : "Management service enabled");
                            fetchData();
                          } catch (error) {
                            toast.error("Failed to update");
                          }
                        }}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          property.management_service_enabled 
                            ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" 
                            : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                        }`}
                        data-testid="management-toggle"
                      >
                        {property.management_service_enabled ? "✓ Enabled" : "Disabled"}
                      </button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Dynamic Content Based on Active Section */}
            {activeSection === 'overview' && (
              <div className="grid lg:grid-cols-3 gap-6">
                {/* Property Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Home className="w-5 h-5 text-blue-600" />
                      Property Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div className="bg-slate-50 p-3 rounded-lg">
                        <BedDouble className="w-5 h-5 mx-auto text-blue-600 mb-1" />
                        <p className="text-lg font-bold">{property.bedrooms || "-"}</p>
                        <p className="text-xs text-muted-foreground">Beds</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded-lg">
                        <Bath className="w-5 h-5 mx-auto text-blue-600 mb-1" />
                        <p className="text-lg font-bold">{property.bathrooms || "-"}</p>
                        <p className="text-xs text-muted-foreground">Baths</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded-lg">
                        <Sofa className="w-5 h-5 mx-auto text-blue-600 mb-1" />
                        <p className="text-lg font-bold">{property.receptions || "-"}</p>
                        <p className="text-xs text-muted-foreground">Recepts</p>
                      </div>
                    </div>
                    <div className="space-y-2 pt-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Property Type</span>
                        <span className="font-medium capitalize">{property.property_type || "Not Specified"}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Furnished</span>
                        <span className="font-medium capitalize">{property.furnished?.replace('_', ' ') || "Not Specified"}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Floor Area</span>
                        <span className="font-medium">{property.floor_area || "Not specified"}</span>
                      </div>
                      <div className="flex justify-between text-sm pt-2 border-t">
                        <span className="text-muted-foreground">HMO Property</span>
                        <Badge variant={property.is_hmo ? "destructive" : "secondary"}>
                          {property.is_hmo ? "Yes" : "No"}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Current Tenant Card */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <User className="w-5 h-5 text-emerald-600" />
                        Current Tenant
                      </CardTitle>
                      <div className="flex gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="h-8 px-2"
                          onClick={() => setActiveSection('tenant')}
                          data-testid="manage-tenant-btn"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {tenant ? (
                      <div>
                        <div 
                          className="cursor-pointer hover:bg-slate-50 p-3 rounded-lg -mx-3 -mt-1"
                          onClick={() => navigate(`/admin/lettings/tenant/${tenant.tenant_id}`)}
                        >
                          <p className="font-medium text-blue-600">{tenant.name}</p>
                          <p className="text-sm text-muted-foreground">{tenant.email}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant={tenant.status === "active" ? "default" : "secondary"}>
                              {tenant.status}
                            </Badge>
                          </div>
                        </div>
                        
                        <div className="border-t mt-3 pt-3">
                          <p className="text-xs font-medium text-muted-foreground mb-2">PORTAL ACCESS</p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {tenant.portal_opened ? (
                                <div className="flex items-center gap-1 text-green-600">
                                  <CheckCircle className="w-4 h-4" />
                                  <span className="text-sm font-medium">Active</span>
                                </div>
                              ) : tenant.portal_invited ? (
                                <div className="flex items-center gap-1 text-blue-600">
                                  <CheckCircle className="w-4 h-4" />
                                  <span className="text-sm">Invited</span>
                                </div>
                              ) : (
                                <span className="text-sm text-muted-foreground">Not invited</span>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              {!tenant.portal_opened && tenant.email && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 px-2"
                                  onClick={(e) => { e.stopPropagation(); handleSendTenantInvite(); }}
                                  disabled={sendingTenantInvite}
                                >
                                  {sendingTenantInvite ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : property.tenant_name ? (
                      <div>
                        <div className="p-3 rounded-lg -mx-3 -mt-1 bg-amber-50 border border-amber-200">
                          <p className="font-medium">{property.tenant_name}</p>
                          {property.tenant_email && <p className="text-sm text-muted-foreground">{property.tenant_email}</p>}
                          {property.tenant_phone && <p className="text-sm text-muted-foreground">{property.tenant_phone}</p>}
                          <Badge variant="outline" className="mt-2 text-amber-700 border-amber-300">From Property Data</Badge>
                        </div>
                        {property.tenant_email && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-xs font-medium text-muted-foreground mb-2">PORTAL ACCESS</p>
                            <Button
                              size="sm"
                              variant="outline"
                              className="w-full"
                              onClick={handleSendTenantInvite}
                              disabled={sendingTenantInvite}
                            >
                              {sendingTenantInvite ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                              Send Portal Invite
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <User className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                        <p className="text-muted-foreground mb-3">No active tenant</p>
                        <div className="flex gap-2 justify-center">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              fetchAvailableTenants();
                              setShowAllocateTenantModal(true);
                            }}
                          >
                            <User className="w-4 h-4 mr-1" />
                            Allocate
                          </Button>
                          <Button 
                            size="sm"
                            onClick={() => setShowAddTenantModal(true)}
                          >
                            <UserPlus className="w-4 h-4 mr-1" />
                            Add New
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Landlord Card */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <User className="w-5 h-5 text-purple-600" />
                      Landlord
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {landlord ? (
                      <div>
                        <div 
                          className="cursor-pointer hover:bg-slate-50 p-3 rounded-lg -mx-3 -mt-1"
                          onClick={() => navigate(`/admin/lettings/landlord/${landlord.landlord_id}`)}
                        >
                          <p className="font-medium text-blue-600">{landlord.name}</p>
                          <p className="text-sm text-muted-foreground">{landlord.email || property?.landlord_email}</p>
                          {(landlord.phone || property?.landlord_phone) && (
                            <p className="text-sm text-muted-foreground">{landlord.phone || property?.landlord_phone}</p>
                          )}
                        </div>
                        
                        <div className="border-t mt-3 pt-3">
                          <p className="text-xs font-medium text-muted-foreground mb-2">PORTAL ACCESS</p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {landlord.portal_opened ? (
                                <div className="flex items-center gap-1 text-green-600">
                                  <CheckCircle className="w-4 h-4" />
                                  <span className="text-sm font-medium">Active</span>
                                </div>
                              ) : landlord.portal_invited ? (
                                <div className="flex items-center gap-1 text-purple-600">
                                  <CheckCircle className="w-4 h-4" />
                                  <span className="text-sm">Invited</span>
                                </div>
                              ) : (
                                <span className="text-sm text-muted-foreground">Not invited</span>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              {!landlord.portal_opened && (landlord.email || property?.landlord_email) && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 px-2"
                                  onClick={(e) => { e.stopPropagation(); handleSendLandlordInvite(); }}
                                  disabled={sendingLandlordInvite}
                                  data-testid="send-landlord-portal-invite"
                                >
                                  {sendingLandlordInvite ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Change/Remove Landlord Buttons */}
                        <div className="border-t mt-3 pt-3">
                          {/* Bank Details Status */}
                          <div className="flex items-center justify-between mb-3 p-2 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <Landmark className="w-4 h-4 text-slate-500" />
                              <span className="text-sm text-slate-600">Bank Details</span>
                            </div>
                            {landlord.bank_account_number ? (
                              <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Set
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                Not Set
                              </Badge>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setShowAllocateLandlordModal(true)}
                              className="flex-1"
                              data-testid="change-landlord-btn"
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Change
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={handleRemoveLandlord}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid="remove-landlord-btn"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : property.landlord_name ? (
                      <div>
                        <div className="p-3 rounded-lg -mx-3 -mt-1 bg-purple-50 border border-purple-200">
                          <p className="font-medium">{property.landlord_name}</p>
                          {property.landlord_email && <p className="text-sm text-muted-foreground">{property.landlord_email}</p>}
                          {property.landlord_phone && <p className="text-sm text-muted-foreground">{property.landlord_phone}</p>}
                          <Badge variant="outline" className="mt-2 text-purple-700 border-purple-300">From Property Data</Badge>
                        </div>
                        {property.landlord_email && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-xs font-medium text-muted-foreground mb-2">PORTAL ACCESS</p>
                            <Button
                              size="sm"
                              variant="outline"
                              className="w-full"
                              onClick={handleSendLandlordInvite}
                              disabled={sendingLandlordInvite}
                            >
                              {sendingLandlordInvite ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                              Send Portal Invite
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-muted-foreground mb-3">No landlord linked</p>
                        <Button
                          variant="outline"
                          onClick={() => setShowAllocateLandlordModal(true)}
                          data-testid="allocate-landlord-btn"
                        >
                          <UserPlus className="w-4 h-4 mr-2" />
                          Allocate Landlord
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {activeSection === 'contacts' && (
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Landlord Contact Details */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <User className="w-5 h-5 text-purple-600" />
                      Landlord Contact
                    </CardTitle>
                    <CardDescription>Contact details for compliance alerts & notifications</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Landlord Name</Label>
                      {editing ? (
                        <Input
                          value={editData.landlord_name || ""}
                          onChange={(e) => setEditData({ ...editData, landlord_name: e.target.value })}
                          placeholder="Full name"
                          data-testid="landlord-name-input"
                        />
                      ) : (
                        <p className="text-sm font-medium">{property.landlord_name || landlord?.name || "Not set"}</p>
                      )}
                    </div>
                    <div>
                      <Label>Landlord Email</Label>
                      {editing ? (
                        <Input
                          type="email"
                          value={editData.landlord_email || ""}
                          onChange={(e) => setEditData({ ...editData, landlord_email: e.target.value })}
                          placeholder="landlord@example.com"
                          data-testid="landlord-email-input"
                        />
                      ) : (
                        <p className="text-sm">{property.landlord_email || landlord?.email || "Not set"}</p>
                      )}
                    </div>
                    <div>
                      <Label>Landlord Mobile <span className="text-xs text-amber-600">(Required for SMS alerts)</span></Label>
                      {editing ? (
                        <Input
                          type="tel"
                          value={editData.landlord_mobile || ""}
                          onChange={(e) => setEditData({ ...editData, landlord_mobile: e.target.value })}
                          placeholder="07700 900000"
                          data-testid="landlord-mobile-input"
                        />
                      ) : (
                        <div className="flex items-center gap-2">
                          <p className="text-sm">{property.landlord_mobile || "Not set"}</p>
                          {!property.landlord_mobile && (
                            <Badge variant="destructive" className="text-xs">SMS alerts disabled</Badge>
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      <Label>Landlord Phone (Landline)</Label>
                      {editing ? (
                        <Input
                          type="tel"
                          value={editData.landlord_phone || ""}
                          onChange={(e) => setEditData({ ...editData, landlord_phone: e.target.value })}
                          placeholder="01234 567890"
                          data-testid="landlord-phone-input"
                        />
                      ) : (
                        <p className="text-sm">{property.landlord_phone || "Not set"}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Tenant Contact Details */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Home className="w-5 h-5 text-blue-600" />
                          Tenant Contact
                        </CardTitle>
                        <CardDescription>Contact details for appointments & access</CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            fetchAvailableTenants();
                            setShowAllocateTenantModal(true);
                          }} 
                          data-testid="allocate-tenant-btn"
                        >
                          <User className="w-4 h-4 mr-1" />
                          Allocate Existing
                        </Button>
                        <Button size="sm" onClick={() => setShowAddTenantModal(true)} data-testid="add-tenant-btn">
                          <UserPlus className="w-4 h-4 mr-1" />
                          {(tenant || property.tenant_name) ? 'New Tenant' : 'Add Tenant'}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {(!tenant && !property.tenant_name) ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <User className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p className="font-medium">No tenant assigned</p>
                        <p className="text-sm mt-1">Add a new tenant or allocate an existing one to this property</p>
                      </div>
                    ) : (
                      <>
                        <div>
                          <Label>Tenant Name</Label>
                          {editing ? (
                            <Input
                              value={editData.tenant_name || ""}
                              onChange={(e) => setEditData({ ...editData, tenant_name: e.target.value })}
                              placeholder="Full name"
                              data-testid="tenant-name-input"
                            />
                          ) : (
                            <p className="text-sm font-medium">{property.tenant_name || tenant?.name || "Not set"}</p>
                          )}
                        </div>
                        <div>
                          <Label>Tenant Email</Label>
                          {editing ? (
                            <Input
                              type="email"
                              value={editData.tenant_email || ""}
                              onChange={(e) => setEditData({ ...editData, tenant_email: e.target.value })}
                              placeholder="tenant@example.com"
                              data-testid="tenant-email-input"
                            />
                          ) : (
                            <p className="text-sm">{property.tenant_email || tenant?.email || "Not set"}</p>
                          )}
                        </div>
                    <div>
                      <Label>Tenant Mobile <span className="text-xs text-amber-600">(Required for SMS)</span></Label>
                      {editing ? (
                        <Input
                          type="tel"
                          value={editData.tenant_mobile || ""}
                          onChange={(e) => setEditData({ ...editData, tenant_mobile: e.target.value })}
                          placeholder="07700 900000"
                          data-testid="tenant-mobile-input"
                        />
                      ) : (
                        <div className="flex items-center gap-2">
                          <p className="text-sm">{property.tenant_mobile || "Not set"}</p>
                          {!property.tenant_mobile && (
                            <Badge variant="outline" className="text-xs">SMS disabled</Badge>
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      <Label>Tenant Phone (Landline)</Label>
                      {editing ? (
                        <Input
                          type="tel"
                          value={editData.tenant_phone || ""}
                          onChange={(e) => setEditData({ ...editData, tenant_phone: e.target.value })}
                          placeholder="01234 567890"
                          data-testid="tenant-phone-input"
                        />
                      ) : (
                        <p className="text-sm">{property.tenant_phone || "Not set"}</p>
                      )}
                    </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Inspection Provider */}
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ClipboardList className="w-5 h-5 text-emerald-600" />
                      Inspection Provider
                    </CardTitle>
                    <CardDescription>Assigned inspector for inventory & property inspections</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label>Provider Name</Label>
                        {editing ? (
                          <Input
                            value={editData.inspection_provider || ""}
                            onChange={(e) => setEditData({ ...editData, inspection_provider: e.target.value })}
                            placeholder="e.g. Stacy (Inventory)"
                            data-testid="inspection-provider-input"
                          />
                        ) : (
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium">{property.inspection_provider || "Not assigned"}</p>
                            {!property.inspection_provider && (
                              <Badge variant="outline" className="text-xs">No inspector</Badge>
                            )}
                          </div>
                        )}
                      </div>
                      <div>
                        <Label>Provider Email</Label>
                        {editing ? (
                          <Input
                            type="email"
                            value={editData.inspection_provider_email || ""}
                            onChange={(e) => setEditData({ ...editData, inspection_provider_email: e.target.value })}
                            placeholder="inspector@example.com"
                            data-testid="inspection-email-input"
                          />
                        ) : (
                          <p className="text-sm">{property.inspection_provider_email || "Not set"}</p>
                        )}
                      </div>
                      <div>
                        <Label>Provider Phone</Label>
                        {editing ? (
                          <Input
                            type="tel"
                            value={editData.inspection_provider_phone || ""}
                            onChange={(e) => setEditData({ ...editData, inspection_provider_phone: e.target.value })}
                            placeholder="07700 900000"
                            data-testid="inspection-phone-input"
                          />
                        ) : (
                          <p className="text-sm">{property.inspection_provider_phone || "Not set"}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Access Notes */}
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Key className="w-5 h-5 text-amber-600" />
                      Property Access
                    </CardTitle>
                    <CardDescription>Instructions for contractors & inspectors</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Access Notes</Label>
                      {editing ? (
                        <Textarea
                          value={editData.access_notes || ""}
                          onChange={(e) => setEditData({ ...editData, access_notes: e.target.value })}
                          placeholder="e.g. Keys held at office, tenant works from home Mon-Wed, entry via side gate..."
                          rows={3}
                          data-testid="access-notes-input"
                        />
                      ) : (
                        <p className="text-sm whitespace-pre-wrap">{property.access_notes || "No access notes recorded"}</p>
                      )}
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Key Location</Label>
                        {editing ? (
                          <Input
                            value={editData.key_location || ""}
                            onChange={(e) => setEditData({ ...editData, key_location: e.target.value })}
                            placeholder="e.g. Office - Hook 12"
                            data-testid="key-location-input"
                          />
                        ) : (
                          <p className="text-sm">{property.key_location || "Not specified"}</p>
                        )}
                      </div>
                      <div>
                        <Label>Alarm Code</Label>
                        {editing ? (
                          <Input
                            value={editData.alarm_code || ""}
                            onChange={(e) => setEditData({ ...editData, alarm_code: e.target.value })}
                            placeholder="Enter alarm code"
                            data-testid="alarm-code-input"
                          />
                        ) : (
                          <p className="text-sm font-mono">{property.alarm_code || "N/A"}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeSection === 'details' && (
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Tenancy Details - Rent, Deposit, Dates */}
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <PoundSterling className="w-5 h-5 text-emerald-600" />
                      Tenancy Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <Label>Monthly Rent (£)</Label>
                        {editing ? (
                          <Input
                            type="number"
                            value={editData.rent_amount || editData.rent || ""}
                            onChange={(e) => setEditData({ ...editData, rent_amount: parseFloat(e.target.value) || 0, rent: parseFloat(e.target.value) || 0 })}
                            placeholder="e.g. 1200"
                          />
                        ) : (
                          <p className="text-sm font-medium text-emerald-600">£{(property.rent_amount || property.rent || 0).toLocaleString()} pcm</p>
                        )}
                      </div>
                      <div>
                        <Label>Deposit (£)</Label>
                        {editing ? (
                          <Input
                            type="number"
                            value={editData.deposit_amount || editData.deposit || ""}
                            onChange={(e) => setEditData({ ...editData, deposit_amount: parseFloat(e.target.value) || 0 })}
                            placeholder="e.g. 1200"
                          />
                        ) : (
                          <p className="text-sm font-medium">£{(property.deposit_amount || property.deposit || 0).toLocaleString()}</p>
                        )}
                      </div>
                      <div>
                        <Label>Tenancy Start Date</Label>
                        {editing ? (
                          <Input
                            type="date"
                            value={editData.tenancy_start_date || ""}
                            onChange={(e) => setEditData({ ...editData, tenancy_start_date: e.target.value })}
                          />
                        ) : (
                          <p className="text-sm font-medium">{property.tenancy_start_date || "-"}</p>
                        )}
                      </div>
                      <div>
                        <Label>Tenancy End Date</Label>
                        {editing ? (
                          <Input
                            type="date"
                            value={editData.tenancy_end_date || ""}
                            onChange={(e) => setEditData({ ...editData, tenancy_end_date: e.target.value })}
                          />
                        ) : (
                          <p className="text-sm font-medium">{property.tenancy_end_date || (property.is_periodic ? "Periodic" : "-")}</p>
                        )}
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                      <div>
                        <Label>Deposit Scheme</Label>
                        {editing ? (
                          <Select
                            value={editData.deposit_scheme || ""}
                            onValueChange={(v) => setEditData({ ...editData, deposit_scheme: v })}
                          >
                            <SelectTrigger><SelectValue placeholder="Select scheme" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="DPS">DPS</SelectItem>
                              <SelectItem value="TDS">TDS</SelectItem>
                              <SelectItem value="MyDeposits">MyDeposits</SelectItem>
                              <SelectItem value="Protection Scheme">Protection Scheme</SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="text-sm font-medium">{property.deposit_scheme || "Not specified"}</p>
                        )}
                      </div>
                      <div>
                        <Label>Is Periodic?</Label>
                        {editing ? (
                          <Select
                            value={editData.is_periodic ? "yes" : "no"}
                            onValueChange={(v) => setEditData({ ...editData, is_periodic: v === "yes" })}
                          >
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="yes">Yes - Rolling tenancy</SelectItem>
                              <SelectItem value="no">No - Fixed term</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="text-sm font-medium">{property.is_periodic ? "Yes - Rolling" : "No - Fixed term"}</p>
                        )}
                      </div>
                      <div>
                        <Label>Letting Fee (£)</Label>
                        {editing ? (
                          <Input
                            type="number"
                            value={editData.letting_fee || ""}
                            onChange={(e) => setEditData({ ...editData, letting_fee: parseFloat(e.target.value) || 0 })}
                            placeholder="e.g. 300"
                          />
                        ) : (
                          <p className="text-sm font-medium">£{(property.letting_fee || 0).toLocaleString()}</p>
                        )}
                      </div>
                      <div>
                        <Label>Management Fee</Label>
                        {editing ? (
                          <Input
                            value={editData.management_fee_percent ? `${editData.management_fee_percent}%` : (editData.management_fee || "")}
                            onChange={(e) => {
                              const val = e.target.value.replace('%', '');
                              setEditData({ ...editData, management_fee_percent: parseFloat(val) || 0 });
                            }}
                            placeholder="e.g. 10%"
                          />
                        ) : (
                          <p className="text-sm font-medium">{property.management_fee_percent ? `${property.management_fee_percent}%` : (property.management_fee || "Not set")}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Tenure */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="w-5 h-5 text-purple-600" />
                      Tenure
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Tenure Type</Label>
                      {editing ? (
                        <Select
                          value={editData.tenure || ""}
                          onValueChange={(v) => setEditData({ ...editData, tenure: v })}
                        >
                          <SelectTrigger><SelectValue placeholder="Select tenure" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="freehold">Freehold</SelectItem>
                            <SelectItem value="leasehold">Leasehold</SelectItem>
                            <SelectItem value="share_of_freehold">Share of Freehold</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="text-sm font-medium capitalize">{property.tenure || "Not specified"}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Local Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-slate-600" />
                      Local Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Local Authority</Label>
                      {editing ? (
                        <Input
                          value={editData.local_authority || ""}
                          onChange={(e) => setEditData({ ...editData, local_authority: e.target.value })}
                          placeholder="e.g. Bedford Borough Council"
                        />
                      ) : (
                        <p className="text-sm">{property.local_authority || "Not specified"}</p>
                      )}
                    </div>
                    <div>
                      <Label>Council Tax Band</Label>
                      {editing ? (
                        <Select
                          value={editData.council_tax_band || ""}
                          onValueChange={(v) => setEditData({ ...editData, council_tax_band: v })}
                        >
                          <SelectTrigger><SelectValue placeholder="Select band" /></SelectTrigger>
                          <SelectContent>
                            {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].map(band => (
                              <SelectItem key={band} value={band}>Band {band}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="text-sm font-medium">{property.council_tax_band ? `Band ${property.council_tax_band}` : "Not specified"}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Access & Viewing */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Key className="w-5 h-5 text-amber-600" />
                      Access & Viewing
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Viewing Arrangements</Label>
                      {editing ? (
                        <Textarea
                          value={editData.viewing_arrangements || ""}
                          onChange={(e) => setEditData({ ...editData, viewing_arrangements: e.target.value })}
                          rows={2}
                          placeholder="e.g. Accompanied viewings only"
                        />
                      ) : (
                        <p className="text-sm">{property.viewing_arrangements || "Not specified"}</p>
                      )}
                    </div>
                    <div>
                      <Label>Key Location</Label>
                      {editing ? (
                        <Input
                          value={editData.key_location || ""}
                          onChange={(e) => setEditData({ ...editData, key_location: e.target.value })}
                          placeholder="e.g. In office safe"
                        />
                      ) : (
                        <p className="text-sm">{property.key_location || "Not specified"}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Outside Space */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <TreePine className="w-5 h-5 text-green-600" />
                      Outside Space
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Garden</Label>
                      {editing ? (
                        <Input
                          value={editData.garden || ""}
                          onChange={(e) => setEditData({ ...editData, garden: e.target.value })}
                          placeholder="e.g. Private rear garden"
                        />
                      ) : (
                        <p className="text-sm">{property.garden || "None specified"}</p>
                      )}
                    </div>
                    <div>
                      <Label className="flex items-center gap-2">
                        <Car className="w-4 h-4" /> Parking
                      </Label>
                      {editing ? (
                        <Input
                          value={editData.parking || ""}
                          onChange={(e) => setEditData({ ...editData, parking: e.target.value })}
                          placeholder="e.g. Driveway for 2 cars"
                        />
                      ) : (
                        <p className="text-sm">{property.parking || "None specified"}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeSection === 'receipts' && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Receipt className="w-5 h-5 text-emerald-600" />
                      Tenant Receipts
                    </CardTitle>
                    <Button size="sm" onClick={() => setShowReceiptModal(true)} data-testid="record-receipt-btn">
                      <Plus className="w-4 h-4 mr-1" /> Record Receipt
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Receipt Summary */}
                  <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">Total Received</p>
                      <p className="text-xl font-bold text-emerald-600">£{totalReceived.toLocaleString()}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">Allocated</p>
                      <p className="text-xl font-bold text-blue-600">£{totalAllocated.toLocaleString()}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">Monthly Rent</p>
                      <p className="text-xl font-bold text-slate-600">£{monthlyRent.toLocaleString()}</p>
                    </div>
                  </div>

                  {receipts.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Receipt className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p className="font-medium">No receipts recorded</p>
                      <p className="text-sm mt-1">Click &quot;Record Receipt&quot; to add a payment</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-slate-50">
                            <th className="text-left py-3 px-4 font-medium">Reference</th>
                            <th className="text-left py-3 px-4 font-medium">Date</th>
                            <th className="text-left py-3 px-4 font-medium">Method</th>
                            <th className="text-right py-3 px-4 font-medium">Amount</th>
                            <th className="text-center py-3 px-4 font-medium">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {receipts.map((receipt) => (
                            <tr key={receipt.receipt_id} className="border-b hover:bg-slate-50">
                              <td className="py-3 px-4 font-mono text-xs">{receipt.reference}</td>
                              <td className="py-3 px-4">
                                {new Date(receipt.date_received).toLocaleDateString('en-GB')}
                              </td>
                              <td className="py-3 px-4 capitalize">
                                {receipt.payment_method?.replace('_', ' ')}
                              </td>
                              <td className="py-3 px-4 text-right font-medium text-emerald-600">
                                £{receipt.amount?.toFixed(2)}
                              </td>
                              <td className="py-3 px-4 text-center">
                                {receipt.amount_allocated >= receipt.amount ? (
                                  <Badge className="bg-green-100 text-green-700">Allocated</Badge>
                                ) : receipt.amount_allocated > 0 ? (
                                  <Badge className="bg-amber-100 text-amber-700">Partial</Badge>
                                ) : (
                                  <Badge variant="secondary">Unallocated</Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {activeSection === 'statements' && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="w-5 h-5 text-purple-600" />
                      Landlord Statements
                    </CardTitle>
                    <Button size="sm" variant="outline" onClick={() => navigate('/accounts/statements')}>
                      View All Statements
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {statements.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p className="font-medium">No statements generated</p>
                      <p className="text-sm mt-1">Statements are generated monthly</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {statements.slice(0, 5).map((stmt) => (
                        <div key={stmt.statement_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium">{stmt.period || stmt.month}</p>
                            <p className="text-sm text-muted-foreground">
                              {new Date(stmt.created_at).toLocaleDateString('en-GB')}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-emerald-600">£{(stmt.net_amount || stmt.total || 0).toLocaleString()}</p>
                            <Badge variant={stmt.status === 'paid' ? 'default' : 'secondary'}>
                              {stmt.status || 'pending'}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {activeSection === 'rent_schedule' && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    Rent Schedule
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
                      <div>
                        <p className="text-xs text-muted-foreground">Monthly Rent</p>
                        <p className="text-xl font-bold text-emerald-600">£{monthlyRent.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Payment Due</p>
                        <p className="text-lg font-medium">{property.rent_due_day || '1st'} of month</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Payment Method</p>
                        <p className="text-lg font-medium capitalize">{property.payment_method || 'Standing Order'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Annual Rent</p>
                        <p className="text-lg font-bold">£{(monthlyRent * 12).toLocaleString()}</p>
                      </div>
                    </div>
                    
                    <div className="border rounded-lg p-4">
                      <h4 className="font-medium mb-3">Upcoming Rent Due</h4>
                      <div className="space-y-2">
                        {[0, 1, 2].map((i) => {
                          const dueDate = new Date();
                          dueDate.setMonth(dueDate.getMonth() + i);
                          dueDate.setDate(parseInt(property.rent_due_day) || 1);
                          return (
                            <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                              <span className="text-sm">{dueDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                              <span className="font-medium text-emerald-600">£{monthlyRent.toLocaleString()}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {activeSection === 'landlord_fees' && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Percent className="w-5 h-5 text-purple-600" />
                        Landlord Fees
                      </CardTitle>
                      <CardDescription>Fee structure for property management services</CardDescription>
                    </div>
                    {!editing ? (
                      <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-fees-btn">
                        <Edit className="w-4 h-4 mr-1" />
                        Edit Fees
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => { setEditing(false); setEditData(property); }}>
                          Cancel
                        </Button>
                        <Button size="sm" onClick={handleSave} disabled={saving}>
                          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                          Save
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                      <div>
                        <p className="text-xs text-muted-foreground mb-2">Management Fee Type</p>
                        {editing ? (
                          <div className="space-y-3">
                            <div className="flex items-center gap-3">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name="fee_type"
                                  checked={editData.management_fee_type !== 'fixed'}
                                  onChange={() => setEditData({ ...editData, management_fee_type: 'percentage' })}
                                  className="text-purple-600"
                                />
                                <span className="text-sm">Percentage</span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name="fee_type"
                                  checked={editData.management_fee_type === 'fixed'}
                                  onChange={() => setEditData({ ...editData, management_fee_type: 'fixed' })}
                                  className="text-purple-600"
                                />
                                <span className="text-sm">Fixed Amount</span>
                              </label>
                            </div>
                            
                            {editData.management_fee_type === 'fixed' ? (
                              <div className="flex items-center gap-2">
                                <span className="text-lg font-bold text-purple-600">£</span>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={editData.management_fee_fixed || 0}
                                  onChange={(e) => setEditData({ ...editData, management_fee_fixed: parseFloat(e.target.value) || 0 })}
                                  className="w-28"
                                  data-testid="management-fee-fixed-input"
                                />
                                <span className="text-sm text-muted-foreground">per month</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  max="100"
                                  value={editData.management_fee_percent ?? 10}
                                  onChange={(e) => setEditData({ ...editData, management_fee_percent: parseFloat(e.target.value) || 0 })}
                                  className="w-28"
                                  placeholder="Enter %"
                                  data-testid="management-fee-percent-input"
                                />
                                <span className="text-lg font-bold text-purple-600">%</span>
                                <span className="text-sm text-muted-foreground">of rent</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div>
                            {property.management_fee_type === 'fixed' ? (
                              <p className="text-2xl font-bold text-purple-600">£{property.management_fee_fixed || 0}</p>
                            ) : (
                              <p className="text-2xl font-bold text-purple-600">{property.management_fee_percent || 10}%</p>
                            )}
                            <p className="text-sm text-muted-foreground">
                              {property.management_fee_type === 'fixed' ? 'fixed monthly fee' : 'of rent collected'}
                            </p>
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Monthly Fee Amount</p>
                        {editing ? (
                          <p className="text-2xl font-bold">
                            £{editData.management_fee_type === 'fixed' 
                              ? (editData.management_fee_fixed || 0).toFixed(2)
                              : ((monthlyRent * (editData.management_fee_percent || 10)) / 100).toFixed(2)
                            }
                          </p>
                        ) : (
                          <p className="text-2xl font-bold">
                            £{property.management_fee_type === 'fixed'
                              ? (property.management_fee_fixed || 0).toFixed(2)
                              : ((monthlyRent * (property.management_fee_percent || 10)) / 100).toFixed(2)
                            }
                          </p>
                        )}
                        <p className="text-sm text-muted-foreground">+ VAT if applicable</p>
                      </div>
                    </div>
                    
                    <div className="border rounded-lg">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-slate-50">
                            <th className="text-left py-3 px-4 font-medium">Fee Type</th>
                            <th className="text-right py-3 px-4 font-medium">Amount</th>
                            <th className="text-center py-3 px-4 font-medium">VAT</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b">
                            <td className="py-3 px-4">Management Fee</td>
                            <td className="py-3 px-4 text-right">
                              {editing ? (
                                <span>
                                  {editData.management_fee_type === 'fixed' 
                                    ? `£${editData.management_fee_fixed || 0} / month`
                                    : `${editData.management_fee_percent || 10}% of rent`
                                  }
                                </span>
                              ) : (
                                <span>
                                  {property.management_fee_type === 'fixed'
                                    ? `£${property.management_fee_fixed || 0} / month`
                                    : `${property.management_fee_percent || 10}% of rent`
                                  }
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-center">
                              {editing ? (
                                <div className="flex items-center justify-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={editData.vat_registered || false}
                                    onChange={(e) => setEditData({ ...editData, vat_registered: e.target.checked })}
                                    className="rounded"
                                    id="vat-toggle"
                                  />
                                  <Label htmlFor="vat-toggle" className="text-xs">+20% VAT</Label>
                                </div>
                              ) : (
                                <Badge variant={property.vat_registered ? "default" : "secondary"}>
                                  {property.vat_registered ? "+20%" : "N/A"}
                                </Badge>
                              )}
                            </td>
                          </tr>
                          <tr className="border-b">
                            <td className="py-3 px-4">Letting Fee (one-off)</td>
                            <td className="py-3 px-4 text-right">
                              {editing ? (
                                <div className="flex items-center justify-end gap-1">
                                  <span>£</span>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={editData.letting_fee || 0}
                                    onChange={(e) => setEditData({ ...editData, letting_fee: parseFloat(e.target.value) || 0 })}
                                    className="w-24 text-right"
                                    data-testid="letting-fee-input"
                                  />
                                </div>
                              ) : (
                                <span>£{property.letting_fee || 0}</span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <Badge variant={property.vat_registered ? "default" : "secondary"}>
                                {property.vat_registered ? "+20%" : "N/A"}
                              </Badge>
                            </td>
                          </tr>
                          <tr className="border-b">
                            <td className="py-3 px-4">Renewal Fee</td>
                            <td className="py-3 px-4 text-right">
                              {editing ? (
                                <div className="flex items-center justify-end gap-1">
                                  <span>£</span>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={editData.renewal_fee || 75}
                                    onChange={(e) => setEditData({ ...editData, renewal_fee: parseFloat(e.target.value) || 0 })}
                                    className="w-24 text-right"
                                    data-testid="renewal-fee-input"
                                  />
                                </div>
                              ) : (
                                <span>£{property.renewal_fee || 75}</span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <Badge variant={property.vat_registered ? "default" : "secondary"}>
                                {property.vat_registered ? "+20%" : "N/A"}
                              </Badge>
                            </td>
                          </tr>
                          <tr className="border-b">
                            <td className="py-3 px-4">Inventory Fee</td>
                            <td className="py-3 px-4 text-right">
                              {editing ? (
                                <div className="flex items-center justify-end gap-1">
                                  <span>£</span>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={editData.inventory_fee || 0}
                                    onChange={(e) => setEditData({ ...editData, inventory_fee: parseFloat(e.target.value) || 0 })}
                                    className="w-24 text-right"
                                    data-testid="inventory-fee-input"
                                  />
                                </div>
                              ) : (
                                <span>£{property.inventory_fee || 0}</span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <Badge variant={property.vat_registered ? "default" : "secondary"}>
                                {property.vat_registered ? "+20%" : "N/A"}
                              </Badge>
                            </td>
                          </tr>
                          <tr>
                            <td className="py-3 px-4">Checkout Fee</td>
                            <td className="py-3 px-4 text-right">
                              {editing ? (
                                <div className="flex items-center justify-end gap-1">
                                  <span>£</span>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={editData.checkout_fee || 0}
                                    onChange={(e) => setEditData({ ...editData, checkout_fee: parseFloat(e.target.value) || 0 })}
                                    className="w-24 text-right"
                                    data-testid="checkout-fee-input"
                                  />
                                </div>
                              ) : (
                                <span>£{property.checkout_fee || 0}</span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <Badge variant={property.vat_registered ? "default" : "secondary"}>
                                {property.vat_registered ? "+20%" : "N/A"}
                              </Badge>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {editing && (
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm text-blue-700">
                          <strong>Note:</strong> Changes will apply to future statements. Existing statements will not be affected.
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Bank Details Section */}
            {activeSection === 'bank_details' && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Landmark className="w-5 h-5 text-blue-600" />
                        Landlord Bank Details
                      </CardTitle>
                      <CardDescription>Bank account for rent payments to landlord</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      {property.landlord_id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              const headers = { Authorization: `Bearer ${token}` };
                              const res = await axios.get(`${API}/lettings/landlords/${property.landlord_id}`, { headers });
                              const ll = res.data;
                              
                              if (!ll.bank_account_name && !ll.bank_name && !ll.bank_sort_code && !ll.bank_account_number) {
                                toast.error("No bank details found for this landlord");
                                return;
                              }
                              
                              // Update property with landlord's bank details
                              await axios.put(
                                `${API}/lettings/properties/${propertyId}`,
                                {
                                  bank_account_name: ll.bank_account_name || property.bank_account_name,
                                  bank_name: ll.bank_name || property.bank_name,
                                  bank_sort_code: ll.bank_sort_code || property.bank_sort_code,
                                  bank_account_number: ll.bank_account_number || property.bank_account_number
                                },
                                { headers }
                              );
                              toast.success("Bank details pulled from landlord");
                              fetchData();
                            } catch (error) {
                              toast.error("Failed to pull landlord bank details");
                            }
                          }}
                          data-testid="pull-landlord-bank-btn"
                        >
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Pull from Landlord
                        </Button>
                      )}
                      <Button
                        variant={editing ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          if (editing) {
                            handleSave();
                          } else {
                            setEditing(true);
                          }
                        }}
                        disabled={saving}
                        data-testid="edit-bank-details-btn"
                      >
                        {saving ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-1" />
                        ) : editing ? (
                          <Save className="w-4 h-4 mr-1" />
                        ) : (
                          <Edit className="w-4 h-4 mr-1" />
                        )}
                        {editing ? "Save" : "Edit"}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {/* Bank Account Details */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div>
                          <Label>Account Holder Name</Label>
                          {editing ? (
                            <Input
                              value={editData.bank_account_name || ""}
                              onChange={(e) => setEditData({ ...editData, bank_account_name: e.target.value })}
                              placeholder="Name on account"
                              data-testid="bank-account-name-input"
                            />
                          ) : (
                            <p className="text-sm font-medium mt-1">{property.bank_account_name || "Not set"}</p>
                          )}
                        </div>
                        <div>
                          <Label>Bank Name</Label>
                          {editing ? (
                            <Input
                              value={editData.bank_name || ""}
                              onChange={(e) => setEditData({ ...editData, bank_name: e.target.value })}
                              placeholder="e.g. Barclays, HSBC, Lloyds"
                              data-testid="bank-name-input"
                            />
                          ) : (
                            <p className="text-sm font-medium mt-1">{property.bank_name || "Not set"}</p>
                          )}
                        </div>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <Label>Sort Code</Label>
                          {editing ? (
                            <Input
                              value={editData.bank_sort_code || ""}
                              onChange={(e) => setEditData({ ...editData, bank_sort_code: e.target.value })}
                              placeholder="XX-XX-XX"
                              maxLength={8}
                              data-testid="bank-sort-code-input"
                            />
                          ) : (
                            <p className="text-sm font-medium font-mono mt-1">{property.bank_sort_code || "Not set"}</p>
                          )}
                        </div>
                        <div>
                          <Label>Account Number</Label>
                          {editing ? (
                            <Input
                              value={editData.bank_account_number || ""}
                              onChange={(e) => setEditData({ ...editData, bank_account_number: e.target.value })}
                              placeholder="8 digit account number"
                              maxLength={8}
                              data-testid="bank-account-number-input"
                            />
                          ) : (
                            <p className="text-sm font-medium font-mono mt-1">
                              {property.bank_account_number 
                                ? `****${property.bank_account_number.slice(-4)}` 
                                : "Not set"}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Payment Reference */}
                    <div className="border-t pt-4">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <Label>Payment Reference</Label>
                          {editing ? (
                            <Input
                              value={editData.payment_reference || ""}
                              onChange={(e) => setEditData({ ...editData, payment_reference: e.target.value })}
                              placeholder="Reference for bank transfers"
                              data-testid="payment-reference-input"
                            />
                          ) : (
                            <p className="text-sm font-medium mt-1">{property.payment_reference || property.property_id || "Not set"}</p>
                          )}
                        </div>
                        <div>
                          <Label>IBAN (Optional - for international)</Label>
                          {editing ? (
                            <Input
                              value={editData.bank_iban || ""}
                              onChange={(e) => setEditData({ ...editData, bank_iban: e.target.value })}
                              placeholder="GB00 XXXX 0000 0000 0000 00"
                              data-testid="bank-iban-input"
                            />
                          ) : (
                            <p className="text-sm font-medium font-mono mt-1">{property.bank_iban || "Not set"}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Info Box */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div>
                          <p className="text-sm text-blue-800 font-medium">Bank Details Usage</p>
                          <p className="text-sm text-blue-700 mt-1">
                            These details are used for paying rent to the landlord. Ensure they match the landlord's registered bank account exactly.
                            Changes will apply to future payments only.
                          </p>
                        </div>
                      </div>
                    </div>

                    {editing && (
                      <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" onClick={() => {
                          setEditing(false);
                          setEditData(property);
                        }}>
                          Cancel
                        </Button>
                        <Button onClick={handleSave} disabled={saving}>
                          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                          Save Bank Details
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tenant Section */}
            {activeSection === 'tenant' && (
              <div className="space-y-6">
                {/* Current Tenant Info */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Home className="w-5 h-5 text-emerald-600" />
                          Tenant Management
                        </CardTitle>
                        <CardDescription>Manage the tenant assigned to this property</CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            fetchAvailableTenants();
                            setShowAllocateTenantModal(true);
                          }}
                          data-testid="allocate-tenant-section-btn"
                        >
                          <User className="w-4 h-4 mr-1" />
                          Allocate Existing
                        </Button>
                        <Button size="sm" onClick={() => setShowAddTenantModal(true)} data-testid="add-tenant-section-btn">
                          <UserPlus className="w-4 h-4 mr-1" />
                          Add New Tenant
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {tenant ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-6">
                          <div className="space-y-4">
                            <div>
                              <Label className="text-muted-foreground text-xs">TENANT NAME</Label>
                              <p className="font-medium text-lg">{tenant.name}</p>
                            </div>
                            <div>
                              <Label className="text-muted-foreground text-xs">EMAIL</Label>
                              <p className="text-sm">{tenant.email || 'Not set'}</p>
                            </div>
                            <div>
                              <Label className="text-muted-foreground text-xs">MOBILE</Label>
                              <p className="text-sm">{tenant.mobile || 'Not set'}</p>
                            </div>
                            <div>
                              <Label className="text-muted-foreground text-xs">PHONE</Label>
                              <p className="text-sm">{tenant.phone || 'Not set'}</p>
                            </div>
                          </div>
                          <div className="space-y-4">
                            <div>
                              <Label className="text-muted-foreground text-xs">STATUS</Label>
                              <div className="mt-1">
                                <Badge variant={tenant.status === "active" ? "default" : "secondary"}>
                                  {tenant.status === "active" ? "Active Tenant" : tenant.status}
                                </Badge>
                              </div>
                            </div>
                            <div>
                              <Label className="text-muted-foreground text-xs">TENANCY START</Label>
                              <p className="text-sm">{tenant.tenancy_start ? new Date(tenant.tenancy_start).toLocaleDateString('en-GB') : 'Not set'}</p>
                            </div>
                            <div>
                              <Label className="text-muted-foreground text-xs">TENANCY END</Label>
                              <p className="text-sm">{tenant.tenancy_end ? new Date(tenant.tenancy_end).toLocaleDateString('en-GB') : 'Not set'}</p>
                            </div>
                            <div>
                              <Label className="text-muted-foreground text-xs">MONTHLY RENT</Label>
                              <p className="text-sm font-medium">£{tenant.rent_amount?.toLocaleString() || property.rent_amount?.toLocaleString() || '0'}</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="border-t pt-4 mt-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs font-medium text-muted-foreground mb-1">PORTAL ACCESS</p>
                              <div className="flex items-center gap-2">
                                {tenant.portal_opened ? (
                                  <Badge variant="default" className="bg-green-600">Portal Active</Badge>
                                ) : tenant.portal_invited ? (
                                  <Badge variant="outline" className="text-blue-600 border-blue-300">Invite Sent</Badge>
                                ) : (
                                  <Badge variant="outline">Not Invited</Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/admin/lettings/tenant/${tenant.tenant_id}`)}
                              >
                                View Full Profile
                              </Button>
                              {!tenant.portal_opened && tenant.email && (
                                <Button
                                  size="sm"
                                  onClick={handleSendTenantInvite}
                                  disabled={sendingTenantInvite}
                                >
                                  {sendingTenantInvite ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                                  Send Portal Invite
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : property.tenant_name ? (
                      <div className="space-y-4">
                        <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium">{property.tenant_name}</p>
                              {property.tenant_email && <p className="text-sm text-muted-foreground">{property.tenant_email}</p>}
                              {property.tenant_phone && <p className="text-sm text-muted-foreground">{property.tenant_phone}</p>}
                            </div>
                            <Badge variant="outline" className="text-amber-700 border-amber-300">
                              Legacy Data
                            </Badge>
                          </div>
                          <p className="text-xs text-amber-600 mt-2">
                            This tenant data is from a previous import. Create a proper tenant record using the buttons above.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <User className="w-16 h-16 mx-auto mb-4 text-slate-200" />
                        <p className="text-lg font-medium text-slate-600 mb-2">No Tenant Assigned</p>
                        <p className="text-sm text-muted-foreground mb-4">
                          Add a new tenant or allocate an existing one to this property
                        </p>
                        <div className="flex gap-3 justify-center">
                          <Button 
                            variant="outline"
                            onClick={() => {
                              fetchAvailableTenants();
                              setShowAllocateTenantModal(true);
                            }}
                          >
                            <User className="w-4 h-4 mr-2" />
                            Allocate Existing Tenant
                          </Button>
                          <Button onClick={() => setShowAddTenantModal(true)}>
                            <UserPlus className="w-4 h-4 mr-2" />
                            Create New Tenant
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Past Tenants / Tenant History Section */}
            {activeSection === 'tenant_history' && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Users className="w-5 h-5 text-slate-600" />
                      Past Tenants
                    </CardTitle>
                    <CardDescription>
                      Historical record of previous tenants at this property
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {tenantHistory.length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <Users className="w-16 h-16 mx-auto mb-4 opacity-20" />
                        <p className="font-medium text-lg">No Past Tenants</p>
                        <p className="text-sm mt-2">
                          When tenants are archived, their records will appear here.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {tenantHistory.map((pastTenant, index) => (
                          <div 
                            key={pastTenant.tenant_id || index}
                            className="p-4 border rounded-lg bg-slate-50"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                                  <User className="w-5 h-5 text-slate-500" />
                                </div>
                                <div>
                                  <p className="font-semibold">{pastTenant.name}</p>
                                  {pastTenant.email && (
                                    <p className="text-sm text-muted-foreground">{pastTenant.email}</p>
                                  )}
                                </div>
                              </div>
                              <Badge variant="secondary" className="text-xs">
                                Archived
                              </Badge>
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
                              <div>
                                <p className="text-muted-foreground text-xs">Tenancy Period</p>
                                <p className="font-medium">
                                  {pastTenant.start_date || "Unknown"} - {pastTenant.end_date || "Unknown"}
                                </p>
                              </div>
                              {pastTenant.rent_amount && (
                                <div>
                                  <p className="text-muted-foreground text-xs">Rent</p>
                                  <p className="font-medium text-emerald-600">
                                    £{pastTenant.rent_amount?.toLocaleString()} {pastTenant.rent_frequency || "pcm"}
                                  </p>
                                </div>
                              )}
                              {pastTenant.deposit && (
                                <div>
                                  <p className="text-muted-foreground text-xs">Deposit</p>
                                  <p className="font-medium">£{pastTenant.deposit?.toLocaleString()}</p>
                                </div>
                              )}
                              {pastTenant.phone && (
                                <div>
                                  <p className="text-muted-foreground text-xs">Phone</p>
                                  <p className="font-medium">{pastTenant.phone}</p>
                                </div>
                              )}
                            </div>
                            
                            {pastTenant.tenancy_reference && (
                              <div className="mt-3 pt-3 border-t">
                                <p className="text-xs text-muted-foreground">
                                  Ref: {pastTenant.tenancy_reference}
                                </p>
                              </div>
                            )}
                            
                            {pastTenant.deposit_scheme && (
                              <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
                                <span className="font-medium text-blue-700">Deposit Protection:</span>
                                <span className="text-blue-600 ml-1">
                                  {pastTenant.deposit_scheme} 
                                  {pastTenant.deposit_certificate_number && ` - ${pastTenant.deposit_certificate_number}`}
                                </span>
                              </div>
                            )}
                            
                            <div className="mt-3 pt-3 border-t flex items-center justify-between text-xs text-muted-foreground">
                              <span>
                                Archived: {new Date(pastTenant.archived_at).toLocaleDateString('en-GB', { 
                                  day: 'numeric', month: 'short', year: 'numeric' 
                                })}
                              </span>
                              <span>By: {pastTenant.archived_by || "System"}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Inspections Section */}
            {activeSection === 'inspections' && (
              <div className="space-y-6">
                {/* Inventory / Check-in / Check-out Reports */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <FileText className="w-5 h-5 text-purple-600" />
                          Inventory & Check-Out Reports
                        </CardTitle>
                        <CardDescription>Move-in inventories, mid-term inspections and check-out reports with photos, meters, keys & signatures</CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/inventory/reports?property_id=${propertyId}`)}
                          data-testid="view-inventory-reports-btn"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View Reports
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => navigate(`/inventory/reports?property_id=${propertyId}&action=new`)}
                          className="bg-purple-600 hover:bg-purple-700"
                          data-testid="new-inventory-report-btn"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          New Report
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-600">
                      All inventory, mid-term and check-out reports for this property are listed in the Inventory module — click <strong>View Reports</strong> above to open them in context.
                    </p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <ClipboardList className="w-5 h-5 text-orange-600" />
                          Property Inspections
                        </CardTitle>
                        <CardDescription>Schedule and track routine property inspections</CardDescription>
                      </div>
                      <Button size="sm" onClick={() => setShowAddInspectionModal(true)} data-testid="add-inspection-btn">
                        <Plus className="w-4 h-4 mr-1" />
                        Schedule Inspection
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {inspections.length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <ClipboardList className="w-16 h-16 mx-auto mb-4 opacity-20" />
                        <p className="font-medium text-lg">No Inspections Scheduled</p>
                        <p className="text-sm mt-2 mb-4">Set up routine inspections to stay on top of property maintenance</p>
                        <Button onClick={() => setShowAddInspectionModal(true)}>
                          <Plus className="w-4 h-4 mr-2" />
                          Schedule First Inspection
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Upcoming/Due Inspections */}
                        {inspections.filter(i => i.status !== 'completed').length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-muted-foreground mb-3">UPCOMING / DUE</h4>
                            <div className="space-y-3">
                              {inspections
                                .filter(i => i.status !== 'completed')
                                .sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date))
                                .map((inspection) => {
                                  const scheduledDate = new Date(inspection.scheduled_date);
                                  const today = new Date();
                                  const daysUntil = Math.ceil((scheduledDate - today) / (1000 * 60 * 60 * 24));
                                  const isOverdue = daysUntil < 0;
                                  const isDueSoon = daysUntil >= 0 && daysUntil <= inspection.alert_days_before;
                                  
                                  return (
                                    <div 
                                      key={inspection.inspection_id}
                                      className={`p-4 rounded-lg border ${
                                        isOverdue ? 'bg-red-50 border-red-200' :
                                        isDueSoon ? 'bg-amber-50 border-amber-200' :
                                        'bg-slate-50 border-slate-200'
                                      }`}
                                    >
                                      <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2 mb-1">
                                            <span className="font-medium capitalize">{inspection.inspection_type} Inspection</span>
                                            {isOverdue && (
                                              <Badge variant="destructive">
                                                <AlertCircle className="w-3 h-3 mr-1" />
                                                Overdue by {Math.abs(daysUntil)} days
                                              </Badge>
                                            )}
                                            {isDueSoon && !isOverdue && (
                                              <Badge className="bg-amber-500">
                                                <Clock className="w-3 h-3 mr-1" />
                                                Due in {daysUntil} days
                                              </Badge>
                                            )}
                                            {!isDueSoon && !isOverdue && (
                                              <Badge variant="outline">
                                                Scheduled
                                              </Badge>
                                            )}
                                          </div>
                                          <p className="text-sm text-muted-foreground">
                                            Scheduled: {scheduledDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                          </p>
                                          {inspection.notes && (
                                            <p className="text-sm mt-2 text-slate-600">{inspection.notes}</p>
                                          )}
                                          <p className="text-xs text-muted-foreground mt-2">
                                            Repeats every {inspection.frequency_months} month{inspection.frequency_months > 1 ? 's' : ''} • 
                                            Alert {inspection.alert_days_before} days before
                                          </p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleCompleteInspection(inspection.inspection_id)}
                                            data-testid={`complete-inspection-${inspection.inspection_id}`}
                                          >
                                            <CheckCircle className="w-4 h-4 mr-1" />
                                            Complete
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="text-red-500 hover:bg-red-50"
                                            onClick={() => handleDeleteInspection(inspection.inspection_id)}
                                            data-testid={`delete-inspection-${inspection.inspection_id}`}
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        )}

                        {/* Completed Inspections */}
                        {inspections.filter(i => i.status === 'completed').length > 0 && (
                          <div className="mt-6">
                            <h4 className="text-sm font-medium text-muted-foreground mb-3">COMPLETED</h4>
                            <div className="space-y-2">
                              {inspections
                                .filter(i => i.status === 'completed')
                                .sort((a, b) => new Date(b.completed_date || b.scheduled_date) - new Date(a.completed_date || a.scheduled_date))
                                .slice(0, 5)
                                .map((inspection) => (
                                  <div 
                                    key={inspection.inspection_id}
                                    className="p-3 rounded-lg bg-green-50 border border-green-200 flex items-center justify-between"
                                  >
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <CheckCircle className="w-4 h-4 text-green-600" />
                                        <span className="font-medium capitalize">{inspection.inspection_type} Inspection</span>
                                      </div>
                                      <p className="text-xs text-muted-foreground mt-1">
                                        Completed: {new Date(inspection.completed_date || inspection.scheduled_date).toLocaleDateString('en-GB')}
                                      </p>
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-red-500 hover:bg-red-50"
                                      onClick={() => handleDeleteInspection(inspection.inspection_id)}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Inspection Settings Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-blue-600" />
                      Alert Settings
                    </CardTitle>
                    <CardDescription>Default settings for inspection reminders</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {!editingInspectionSettings ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-4 bg-slate-50 rounded-lg">
                            <p className="text-xs text-muted-foreground mb-1">Default Frequency</p>
                            <p className="text-lg font-semibold">Every {inspectionSettings.default_frequency_months} month{inspectionSettings.default_frequency_months > 1 ? 's' : ''}</p>
                            <p className="text-xs text-muted-foreground mt-1">Routine property inspections</p>
                          </div>
                          <div className="p-4 bg-slate-50 rounded-lg">
                            <p className="text-xs text-muted-foreground mb-1">Alert Lead Time</p>
                            <p className="text-lg font-semibold">{inspectionSettings.default_alert_days} days before</p>
                            <p className="text-xs text-muted-foreground mt-1">Email reminder sent to staff</p>
                          </div>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setEditingInspectionSettings(true)}
                          data-testid="edit-inspection-settings-btn"
                        >
                          <Edit className="w-4 h-4 mr-1" /> Edit Settings
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Default Inspection Frequency</Label>
                            <Select 
                              value={String(inspectionSettings.default_frequency_months)} 
                              onValueChange={(val) => setInspectionSettings({ ...inspectionSettings, default_frequency_months: parseInt(val) })}
                            >
                              <SelectTrigger data-testid="default-frequency-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="1">Every 1 month</SelectItem>
                                <SelectItem value="2">Every 2 months</SelectItem>
                                <SelectItem value="3">Every 3 months</SelectItem>
                                <SelectItem value="4">Every 4 months</SelectItem>
                                <SelectItem value="5">Every 5 months</SelectItem>
                                <SelectItem value="6">Every 6 months</SelectItem>
                                <SelectItem value="12">Every 12 months</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground mt-1">Applied to new inspections</p>
                          </div>
                          <div>
                            <Label>Alert Lead Time</Label>
                            <Select 
                              value={String(inspectionSettings.default_alert_days)} 
                              onValueChange={(val) => setInspectionSettings({ ...inspectionSettings, default_alert_days: parseInt(val) })}
                            >
                              <SelectTrigger data-testid="default-alert-days-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="7">7 days before</SelectItem>
                                <SelectItem value="14">14 days before</SelectItem>
                                <SelectItem value="21">21 days before</SelectItem>
                                <SelectItem value="28">28 days before</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground mt-1">Days before due date for reminder</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => {
                              setEditingInspectionSettings(false);
                              // Reset to saved values from property
                              setInspectionSettings({
                                default_frequency_months: property?.default_frequency_months || 3,
                                default_alert_days: property?.default_alert_days || 14
                              });
                            }}
                          >
                            Cancel
                          </Button>
                          <Button 
                            size="sm" 
                            onClick={handleSaveInspectionSettings}
                            disabled={savingInspectionSettings}
                            data-testid="save-inspection-settings-btn"
                          >
                            {savingInspectionSettings ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-1" />
                            ) : (
                              <Save className="w-4 h-4 mr-1" />
                            )}
                            Save Settings
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {activeSection === 'notes' && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <StickyNote className="w-5 h-5 text-amber-600" />
                      Notes
                    </CardTitle>
                    <Button size="sm" onClick={() => setShowAddNoteModal(true)}>
                      <Plus className="w-4 h-4 mr-1" /> Add Note
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {notes.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <StickyNote className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p className="font-medium">No notes</p>
                      <p className="text-sm mt-1">Add notes about this property</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {notes.map((note, idx) => (
                        <div 
                          key={note.note_id || idx} 
                          className={`p-4 rounded-lg border ${note.is_alert ? 'bg-amber-50 border-amber-200' : 'bg-slate-50'}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              {note.is_alert && (
                                <Badge className="bg-amber-100 text-amber-700 mb-2">
                                  <AlertTriangle className="w-3 h-3 mr-1" /> Alert
                                </Badge>
                              )}
                              <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => {
                                  setEditingNote(note);
                                  setShowEditNoteModal(true);
                                }}
                                data-testid={`edit-note-${idx}`}
                              >
                                <Edit className="w-4 h-4 text-slate-500" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 hover:bg-red-50"
                                onClick={() => handleDeleteNote(note.note_id)}
                                data-testid={`delete-note-${idx}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                          <div className="mt-2 text-xs text-muted-foreground">
                            {note.created_by_name || 'System'} • {note.created_at ? new Date(note.created_at).toLocaleString('en-GB') : '-'}
                            {note.updated_at && note.updated_at !== note.created_at && (
                              <span> • Edited {new Date(note.updated_at).toLocaleString('en-GB')}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {activeSection === 'compliance' && (
              <div className="space-y-6">
                {/* Compliance Dates - Editable */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Calendar className="w-5 h-5 text-blue-600" />
                          Compliance Certificates
                        </CardTitle>
                        <CardDescription>Certificate expiry dates - system will send 28-day and 14-day alerts automatically</CardDescription>
                      </div>
                      {!editing && (
                        <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-compliance-btn">
                          <Edit className="w-4 h-4 mr-1" />
                          Edit Dates
                        </Button>
                      )}
                      {editing && (
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => { setEditing(false); setEditData(property); }}>
                            Cancel
                          </Button>
                          <Button size="sm" onClick={handleSave} disabled={saving}>
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                            Save
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-3 gap-6">
                      {/* Gas Safety */}
                      <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="w-5 h-5 text-red-500" />
                          <Label className="font-semibold">Gas Safety (CP12)</Label>
                        </div>
                        {editing ? (
                          <>
                            <div>
                              <Label className="text-xs">Expiry Date</Label>
                              <Input
                                type="date"
                                value={editData.gas_safety_expiry || ""}
                                onChange={(e) => setEditData({ ...editData, gas_safety_expiry: e.target.value })}
                                data-testid="gas-expiry-input"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id="gas_exempt"
                                checked={editData.gas_safety_exempt || false}
                                onChange={(e) => setEditData({ ...editData, gas_safety_exempt: e.target.checked })}
                                className="rounded"
                              />
                              <Label htmlFor="gas_exempt" className="text-xs">Property is gas exempt</Label>
                            </div>
                          </>
                        ) : (
                          <div className="space-y-2">
                            {property.gas_safety_exempt ? (
                              <Badge className="bg-slate-100 text-slate-700">Exempt</Badge>
                            ) : property.gas_safety_expiry ? (
                              <>
                                <p className="text-sm font-medium">{new Date(property.gas_safety_expiry).toLocaleDateString('en-GB')}</p>
                                <Badge className={new Date(property.gas_safety_expiry) < new Date() ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}>
                                  {new Date(property.gas_safety_expiry) < new Date() ? "Expired" : "Valid"}
                                </Badge>
                              </>
                            ) : (
                              <Badge variant="destructive">Not Set</Badge>
                            )}
                          </div>
                        )}
                      </div>

                      {/* EPC */}
                      <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Zap className="w-5 h-5 text-amber-500" />
                          <Label className="font-semibold">EPC Certificate</Label>
                        </div>
                        {editing ? (
                          <>
                            <div>
                              <Label className="text-xs">Expiry Date</Label>
                              <Input
                                type="date"
                                value={editData.epc_expiry_date || ""}
                                onChange={(e) => setEditData({ ...editData, epc_expiry_date: e.target.value })}
                                data-testid="epc-expiry-input"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Rating</Label>
                              <Select
                                value={editData.epc_rating_current || ""}
                                onValueChange={(v) => setEditData({ ...editData, epc_rating_current: v })}
                              >
                                <SelectTrigger><SelectValue placeholder="Select rating" /></SelectTrigger>
                                <SelectContent>
                                  {['A', 'B', 'C', 'D', 'E', 'F', 'G'].map(r => (
                                    <SelectItem key={r} value={r}>{r}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </>
                        ) : (
                          <div className="space-y-2">
                            {property.epc_expiry_date ? (
                              <>
                                <p className="text-sm font-medium">{new Date(property.epc_expiry_date).toLocaleDateString('en-GB')}</p>
                                <div className="flex items-center gap-2">
                                  <div className={`w-8 h-8 rounded flex items-center justify-center text-white text-sm font-bold ${EPC_COLORS[property.epc_rating_current] || 'bg-slate-300'}`}>
                                    {property.epc_rating_current || "?"}
                                  </div>
                                  <Badge className={new Date(property.epc_expiry_date) < new Date() ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}>
                                    {new Date(property.epc_expiry_date) < new Date() ? "Expired" : "Valid"}
                                  </Badge>
                                </div>
                              </>
                            ) : (
                              <Badge variant="destructive">Not Set</Badge>
                            )}
                          </div>
                        )}
                      </div>

                      {/* EICR */}
                      <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Zap className="w-5 h-5 text-blue-500" />
                          <Label className="font-semibold">EICR (Electrical)</Label>
                        </div>
                        {editing ? (
                          <div>
                            <Label className="text-xs">Expiry Date</Label>
                            <Input
                              type="date"
                              value={editData.eicr_expiry || ""}
                              onChange={(e) => setEditData({ ...editData, eicr_expiry: e.target.value })}
                              data-testid="eicr-expiry-input"
                            />
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {property.eicr_expiry ? (
                              <>
                                <p className="text-sm font-medium">{new Date(property.eicr_expiry).toLocaleDateString('en-GB')}</p>
                                <Badge className={new Date(property.eicr_expiry) < new Date() ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}>
                                  {new Date(property.eicr_expiry) < new Date() ? "Expired" : "Valid"}
                                </Badge>
                              </>
                            ) : (
                              <Badge variant="destructive">Not Set</Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Alert Info */}
                    <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-medium text-blue-800">Automatic Compliance Alerts</p>
                          <p className="text-blue-600 mt-1">
                            The system will automatically send email/SMS alerts to the landlord at:
                          </p>
                          <ul className="list-disc list-inside text-blue-600 mt-1 space-y-1">
                            <li><strong>28 days before expiry</strong> - Initial reminder</li>
                            <li><strong>14 days before expiry</strong> - If no response, job will be auto-booked</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Compliance Status Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Shield className="w-5 h-5 text-green-600" />
                      Compliance Status Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      <div className={`p-4 rounded-lg text-center ${property.gas_safety_expiry || property.gas_safety_exempt ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                        <FileText className={`w-8 h-8 mx-auto mb-2 ${property.gas_safety_expiry || property.gas_safety_exempt ? 'text-green-600' : 'text-red-500'}`} />
                        <p className="text-sm font-medium">Gas Safety</p>
                        <Badge variant={property.gas_safety_expiry || property.gas_safety_exempt ? "default" : "destructive"} className="mt-2">
                          {property.gas_safety_exempt ? "Exempt" : property.gas_safety_expiry ? "Valid" : "Missing"}
                        </Badge>
                      </div>
                      <div className={`p-4 rounded-lg text-center ${property.epc_expiry_date ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                        <Zap className={`w-8 h-8 mx-auto mb-2 ${property.epc_expiry_date ? 'text-amber-500' : 'text-red-500'}`} />
                        <p className="text-sm font-medium">EPC</p>
                        <Badge variant={property.epc_expiry_date ? "default" : "destructive"} className="mt-2">
                          {property.epc_rating_current || (property.epc_expiry_date ? "Valid" : "Missing")}
                        </Badge>
                      </div>
                      <div className={`p-4 rounded-lg text-center ${property.eicr_expiry ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                        <Zap className={`w-8 h-8 mx-auto mb-2 ${property.eicr_expiry ? 'text-blue-500' : 'text-red-500'}`} />
                        <p className="text-sm font-medium">EICR</p>
                        <Badge variant={property.eicr_expiry ? "default" : "destructive"} className="mt-2">
                          {property.eicr_expiry ? "Valid" : "Missing"}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Record Receipt Modal */}
      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Record Rent Payment</DialogTitle>
            <DialogDescription>
              {property?.address} - {tenant?.name || "Tenant"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-emerald-50 rounded-lg p-3 text-center">
              <p className="text-xs text-emerald-600">Monthly Rent</p>
              <p className="text-2xl font-bold text-emerald-700">£{monthlyRent.toLocaleString()}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount (£) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={receiptForm.amount}
                  onChange={(e) => setReceiptForm({ ...receiptForm, amount: e.target.value })}
                  placeholder={monthlyRent.toString()}
                  data-testid="receipt-amount-input"
                />
              </div>
              <div>
                <Label>Date Received *</Label>
                <Input
                  type="date"
                  value={receiptForm.date_received}
                  onChange={(e) => setReceiptForm({ ...receiptForm, date_received: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Payment Method</Label>
              <Select
                value={receiptForm.payment_method}
                onValueChange={(v) => setReceiptForm({ ...receiptForm, payment_method: v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="standing_order">Standing Order</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Payment Reference</Label>
              <Input
                value={receiptForm.payment_reference}
                onChange={(e) => setReceiptForm({ ...receiptForm, payment_reference: e.target.value })}
                placeholder="Bank reference or notes"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="paid_direct_prop"
                checked={receiptForm.paid_direct_to_landlord}
                onChange={(e) => setReceiptForm({ ...receiptForm, paid_direct_to_landlord: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="paid_direct_prop" className="text-sm font-normal">
                Paid direct to landlord
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReceiptModal(false)}>Cancel</Button>
            <Button onClick={handleRecordReceipt} disabled={submittingReceipt}>
              {submittingReceipt ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Receipt className="w-4 h-4 mr-2" />}
              Record Receipt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Note Modal */}
      <Dialog open={showAddNoteModal} onOpenChange={setShowAddNoteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
            <DialogDescription>Add a note about this property or letting</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Note Content *</Label>
              <Textarea
                value={newNote.content}
                onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
                rows={4}
                placeholder="Enter your note..."
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_alert"
                checked={newNote.is_alert}
                onChange={(e) => setNewNote({ ...newNote, is_alert: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="is_alert" className="text-sm font-normal">
                Show as alert banner (e.g., &quot;tenant pays on 8th due to UC&quot;)
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddNoteModal(false)}>Cancel</Button>
            <Button onClick={handleAddNote} disabled={savingNote}>
              {savingNote ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Note Modal */}
      <Dialog open={showEditNoteModal} onOpenChange={(open) => {
        setShowEditNoteModal(open);
        if (!open) setEditingNote(null);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Note</DialogTitle>
            <DialogDescription>Update this note</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Note Content *</Label>
              <Textarea
                value={editingNote?.content || ""}
                onChange={(e) => setEditingNote({ ...editingNote, content: e.target.value })}
                rows={4}
                placeholder="Enter your note..."
                data-testid="edit-note-content"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="edit_is_alert"
                checked={editingNote?.is_alert || false}
                onChange={(e) => setEditingNote({ ...editingNote, is_alert: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="edit_is_alert" className="text-sm font-normal">
                Show as alert banner
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditNoteModal(false); setEditingNote(null); }}>Cancel</Button>
            <Button onClick={handleEditNote} disabled={savingNote}>
              {savingNote ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Tenant Modal */}
      <Dialog open={showAddTenantModal} onOpenChange={setShowAddTenantModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Tenant</DialogTitle>
            <DialogDescription>Add a new tenant to this property</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Tenant Name *</Label>
                <Input
                  value={newTenant.name}
                  onChange={(e) => setNewTenant({ ...newTenant, name: e.target.value })}
                  placeholder="Full name"
                  data-testid="new-tenant-name"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newTenant.email}
                  onChange={(e) => setNewTenant({ ...newTenant, email: e.target.value })}
                  placeholder="tenant@example.com"
                  data-testid="new-tenant-email"
                />
              </div>
              <div>
                <Label>Mobile</Label>
                <Input
                  type="tel"
                  value={newTenant.mobile}
                  onChange={(e) => setNewTenant({ ...newTenant, mobile: e.target.value })}
                  placeholder="07700 900000"
                  data-testid="new-tenant-mobile"
                />
              </div>
              <div>
                <Label>Phone (Landline)</Label>
                <Input
                  type="tel"
                  value={newTenant.phone}
                  onChange={(e) => setNewTenant({ ...newTenant, phone: e.target.value })}
                  placeholder="01234 567890"
                  data-testid="new-tenant-phone"
                />
              </div>
              <div>
                <Label>Payment Day</Label>
                <Select 
                  value={newTenant.payment_day} 
                  onValueChange={(val) => setNewTenant({ ...newTenant, payment_day: val })}
                >
                  <SelectTrigger data-testid="new-tenant-payment-day">
                    <SelectValue placeholder="Select day" />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(28)].map((_, i) => (
                      <SelectItem key={i+1} value={String(i+1)}>{i+1}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium mb-3">Tenancy Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tenancy Start Date</Label>
                  <Input
                    type="date"
                    value={newTenant.tenancy_start}
                    onChange={(e) => setNewTenant({ ...newTenant, tenancy_start: e.target.value })}
                    data-testid="new-tenant-start-date"
                  />
                </div>
                <div>
                  <Label>Tenancy End Date</Label>
                  <Input
                    type="date"
                    value={newTenant.tenancy_end}
                    onChange={(e) => setNewTenant({ ...newTenant, tenancy_end: e.target.value })}
                    data-testid="new-tenant-end-date"
                  />
                </div>
                <div>
                  <Label>Monthly Rent (£)</Label>
                  <Input
                    type="number"
                    value={newTenant.rent_amount}
                    onChange={(e) => setNewTenant({ ...newTenant, rent_amount: e.target.value })}
                    placeholder={property?.rent_amount?.toString() || "0"}
                    data-testid="new-tenant-rent"
                  />
                </div>
                <div>
                  <Label>Deposit Amount (£)</Label>
                  <Input
                    type="number"
                    value={newTenant.deposit_amount}
                    onChange={(e) => setNewTenant({ ...newTenant, deposit_amount: e.target.value })}
                    placeholder="0"
                    data-testid="new-tenant-deposit"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTenantModal(false)}>Cancel</Button>
            <Button onClick={handleAddTenant} disabled={savingTenant}>
              {savingTenant ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Add Tenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Allocate Existing Tenant Modal */}
      <Dialog open={showAllocateTenantModal} onOpenChange={(open) => {
        setShowAllocateTenantModal(open);
        if (!open) {
          setSelectedTenantId(null);
          setTenantSearchQuery("");
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Allocate Existing Tenant</DialogTitle>
            <DialogDescription>Search and select a tenant to assign to this property</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Search Tenants</Label>
              <div className="flex gap-2">
                <Input
                  value={tenantSearchQuery}
                  onChange={(e) => setTenantSearchQuery(e.target.value)}
                  placeholder="Search by name or email..."
                  data-testid="tenant-search-input"
                />
                <Button 
                  variant="outline" 
                  onClick={() => fetchAvailableTenants(tenantSearchQuery)}
                  disabled={loadingTenants}
                >
                  {loadingTenants ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
                </Button>
              </div>
            </div>
            
            <div className="max-h-64 overflow-y-auto border rounded-lg">
              {loadingTenants ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : availableTenants.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <User className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No available tenants found</p>
                  <p className="text-xs mt-1">Try searching or create a new tenant instead</p>
                </div>
              ) : (
                <div className="divide-y">
                  {availableTenants.map((t) => (
                    <div
                      key={t.tenant_id}
                      className={`p-3 cursor-pointer hover:bg-slate-50 transition-colors ${
                        selectedTenantId === t.tenant_id ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''
                      }`}
                      onClick={() => setSelectedTenantId(t.tenant_id)}
                      data-testid={`tenant-option-${t.tenant_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{t.name}</p>
                          <p className="text-xs text-slate-500">{t.email || 'No email'}</p>
                          {t.requirements && (
                            <p className="text-xs text-slate-400 mt-1">Looking for: {t.requirements}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <Badge variant="outline" className="text-xs">
                            {t.status === 'looking' ? 'Looking' : t.status}
                          </Badge>
                          {t.rent_budget && (
                            <p className="text-xs text-slate-500 mt-1">Budget: £{t.rent_budget}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {selectedTenantId && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-blue-700">
                  Selected: {availableTenants.find(t => t.tenant_id === selectedTenantId)?.name}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  This tenant will be assigned to {property.address?.split(',')[0]}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAllocateTenantModal(false)}>Cancel</Button>
            <Button onClick={handleAllocateTenant} disabled={savingTenant || !selectedTenantId}>
              {savingTenant ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Allocate Tenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Inspection Modal */}
      <Dialog open={showAddInspectionModal} onOpenChange={setShowAddInspectionModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Schedule Inspection</DialogTitle>
            <DialogDescription>Set up a property inspection with automatic reminders</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Inspection Type</Label>
              <Select 
                value={newInspection.inspection_type} 
                onValueChange={(val) => setNewInspection({ ...newInspection, inspection_type: val })}
              >
                <SelectTrigger data-testid="inspection-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="routine">Routine Inspection</SelectItem>
                  <SelectItem value="move_in">Move-in Inspection</SelectItem>
                  <SelectItem value="move_out">Move-out / Checkout</SelectItem>
                  <SelectItem value="maintenance">Maintenance Check</SelectItem>
                  <SelectItem value="mid_term">Mid-term Inspection</SelectItem>
                  <SelectItem value="annual">Annual Inspection</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Scheduled Date *</Label>
              <Input
                type="date"
                value={newInspection.scheduled_date}
                onChange={(e) => setNewInspection({ ...newInspection, scheduled_date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                data-testid="inspection-date-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Repeat Every</Label>
                <Select 
                  value={String(newInspection.frequency_months)} 
                  onValueChange={(val) => setNewInspection({ ...newInspection, frequency_months: parseInt(val) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 month</SelectItem>
                    <SelectItem value="2">2 months</SelectItem>
                    <SelectItem value="3">3 months</SelectItem>
                    <SelectItem value="4">4 months</SelectItem>
                    <SelectItem value="5">5 months</SelectItem>
                    <SelectItem value="6">6 months</SelectItem>
                    <SelectItem value="12">12 months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Alert Before</Label>
                <Select 
                  value={String(newInspection.alert_days_before)} 
                  onValueChange={(val) => setNewInspection({ ...newInspection, alert_days_before: parseInt(val) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 days</SelectItem>
                    <SelectItem value="14">14 days</SelectItem>
                    <SelectItem value="21">21 days</SelectItem>
                    <SelectItem value="28">28 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={newInspection.notes}
                onChange={(e) => setNewInspection({ ...newInspection, notes: e.target.value })}
                placeholder="Any special instructions or areas to check..."
                rows={3}
                data-testid="inspection-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddInspectionModal(false)}>Cancel</Button>
            <Button onClick={handleAddInspection} disabled={savingInspection}>
              {savingInspection ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Schedule Inspection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Allocate Landlord Modal */}
      <Dialog open={showAllocateLandlordModal} onOpenChange={setShowAllocateLandlordModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Allocate Landlord to Property
            </DialogTitle>
            <DialogDescription>
              Search for an existing landlord to link to this property
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Search Input */}
            <div>
              <Label>Search Landlord</Label>
              <Input
                value={landlordSearchTerm}
                onChange={(e) => {
                  setLandlordSearchTerm(e.target.value);
                  searchLandlords(e.target.value);
                }}
                placeholder="Search by name, email, or phone..."
                data-testid="landlord-search-input"
              />
            </div>

            {/* Search Results */}
            <div className="max-h-64 overflow-y-auto border rounded-lg">
              {searchingLandlords ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : landlordSearchResults.length > 0 ? (
                <div className="divide-y">
                  {landlordSearchResults.map((ll) => (
                    <div
                      key={ll.landlord_id}
                      className="p-3 hover:bg-slate-50 cursor-pointer flex items-center justify-between"
                      onClick={() => handleAllocateLandlord(ll)}
                    >
                      <div>
                        <p className="font-medium">{ll.name}</p>
                        <p className="text-sm text-muted-foreground">{ll.email}</p>
                        {ll.phone && <p className="text-sm text-muted-foreground">{ll.phone}</p>}
                      </div>
                      <Button size="sm" variant="outline" disabled={allocatingLandlord}>
                        {allocatingLandlord ? <Loader2 className="w-4 h-4 animate-spin" /> : "Select"}
                      </Button>
                    </div>
                  ))}
                </div>
              ) : landlordSearchTerm.length >= 2 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No landlords found matching "{landlordSearchTerm}"</p>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p>Type at least 2 characters to search</p>
                </div>
              )}
            </div>

            {/* Current Landlord Info */}
            {(landlord || property?.landlord_name) && (
              <div className="bg-slate-50 border rounded-lg p-3">
                <p className="text-xs font-medium text-muted-foreground mb-1">CURRENT LANDLORD</p>
                <p className="font-medium">{landlord?.name || property?.landlord_name}</p>
                <p className="text-sm text-muted-foreground">{landlord?.email || property?.landlord_email}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAllocateLandlordModal(false);
              setLandlordSearchTerm("");
              setLandlordSearchResults([]);
            }}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PropertyDetailPage;
